/* eslint-disable no-use-before-define */
import { EventEmitter } from 'events';
import cloneDeep from 'lodash.clonedeep';
import debounce from 'lodash.debounce';
import findLastIndexOf from 'lodash.findlastindex';
import uniqBy from 'lodash.uniqby';
import twitterText from 'twitter-text';
import { DraftMethods, getNewDraft } from '~/composer/composer/entities/Draft';
import {
  AppEnvironments,
  MediaTypes,
  SERVICE_GOOGLEBUSINESS,
  SERVICE_INSTAGRAM,
  SERVICE_STARTPAGE,
  SERVICE_YOUTUBE,
} from '~/constants';
import { LinkedInAnnotation } from '~/editor/plugins/linkedin-annotations/LinkedInAnnotations';
import { PostTypeReel, PostTypeStory } from '~/post/constants';
import { Service } from '~/post/types.generated';
import { HC_UTM_PARAMS } from '~/utils/contants';
import { logError } from '~/utils/logError';
import { getOrCreateUploader } from '~/uploads/lib/uploader/UploadersMap';
import AppActionCreators from '../action-creators/AppActionCreators';
import ComposerActionCreators from '../action-creators/ComposerActionCreators';
import NotificationActionCreators from '../action-creators/NotificationActionCreators';
import { shortenDraftLink } from '../action-creators/shortenDraftLink';
import {
  AttachmentTypes,
  ComposerInitiators,
  ErrorTypes,
  InstagramAspectRatioLimits,
  NotificationScopes,
  Services,
} from '../AppConstants';
import AppDispatcher from '../dispatcher';
import { EditorStateProxy } from '../entities/EditorStateProxy';
import {
  getNewAvailableImage,
  getNewGif,
  getNewImage,
  getNewInstagramFeedbackObj,
  getNewLink,
  getNewRetweet,
  getNewSourceLink,
  getNewVideo,
} from '../entities/factories';
import { MastodonParser } from '../lib/parsers/MastodonParser';
import { validateVideo } from '../lib/validation/ValidateVideo';
import * as ValidatorFactory from '../lib/validation/ValidatorFactory';
import { ActionTypes } from '../state/ActionTypes';
import { generateThumbnailFromVideo } from '../utils/DOMUtils';
import events from '../utils/Events';
import { isUrlOnBlocklist } from '../utils/StringUtils';
import AppStore from './AppStore';
import { NotificationStore } from './NotificationStore';

// import { registerStore, sendToMonitor } from '../utils/devtools';

const CHANGE_EVENT = 'change';

export const getInitialState = () => {
  return {
    drafts: Services.map(getNewDraft),
    draftsSharedData: {
      uploadedImages: [],
      uploadedGifs: [],
      processingVideos: new Map(), // uploadId <> [drafId, uploaderInstance]
      uploadedVideos: [],
    },
    meta: {
      lastInteractedWithComposerId: null,
      forceEditorFocus: false,
      activeThreadId: 0,
      isSuggestedMediaHidden: false,
      // used for Mastodon notices
      hasMentionsWithoutServer: false,
    },
  };
};

let state = getInitialState();

// Register with Redux DevTools (uncomment to enable)
// registerStore('composer', state);

const softResetState = () => {
  const newState = getInitialState();

  // The only data to preserve is the enabled state of each draft
  newState.drafts = newState.drafts.map((newDraft) => {
    const { isEnabled, editorState } = state.drafts.find(
      ({ id }) => id === newDraft.id
    );
    newDraft.isEnabled = isEnabled;
    newDraft.editorState = EditorStateProxy.resetEditorState(editorState);
    return newDraft;
  });

  state = newState;
};

const eventShowSwitchPlanModal = () => {
  events.trigger('show-switch-plan-modal');
};

const ComposerStore = {
  ...EventEmitter.prototype,
  emitChange: () => ComposerStore.emit(CHANGE_EVENT),
  addChangeListener: (callback) => ComposerStore.on(CHANGE_EVENT, callback),
  removeChangeListener: (callback) =>
    ComposerStore.removeListener(CHANGE_EVENT, callback),

  getEnabledDrafts: () => state.drafts.filter((draft) => draft.isEnabled),
  getDrafts: () => state.drafts,

  getInvalidEnabledDraftsFeedback: ({ isDraft = false }) => {
    return ComposerStore.getEnabledDrafts().reduce(
      (invalidDraftsFeedback, draft) => {
        const validationErrors = ValidatorFactory.validate(
          draft,
          isDraft,
          ComposerStore.getActiveThreadId()
        );
        if (validationErrors.length > 0) {
          invalidDraftsFeedback.push({
            draft,
            messages: validationErrors,
          });
        }
        return invalidDraftsFeedback;
      },
      []
    );
  },

  getIsSuggestedMediaBoxHidden: () => state.meta.isSuggestedMediaHidden,
  getDraft: (id) => state.drafts.find((draft) => draft.id === id),
  getDraftText: (id) => {
    const { editorState } = ComposerStore.getDraft(id);
    return editorState ? EditorStateProxy.getPlainText(editorState) : '';
  },

  getDraftLinkUrl: (id) => {
    const { link } = ComposerStore.getDraft(id);
    return link ? link.url : null;
  },

  doesDraftHaveLinkAttachment: (id) => ComposerStore.getDraft(id).link !== null,
  doesDraftHaveRetweetAttachment: (id) =>
    ComposerStore.getDraft(id).retweet !== null,
  doesDraftHaveLinkAttachmentEnabled: (id) =>
    ComposerStore.getDraft(id).enabledAttachmentType === AttachmentTypes.LINK,
  doesDraftHaveSourceLink: (id) =>
    ComposerStore.getDraft(id).sourceLink !== null,

  isDraftEnabled: (id) => ComposerStore.getDraft(id).isEnabled,
  isDraftLocked: (id) => ComposerStore.getDraft(id).isSaved,

  getDraftsSharedData: () => state.draftsSharedData,

  areAllDraftsSaved: () => {
    const enabledDrafts = ComposerStore.getEnabledDrafts();
    return (
      enabledDrafts.length > 0 && enabledDrafts.every((draft) => draft.isSaved)
    );
  },

  // Retrieve long link from shortLinkLongLinkMap (if url is long link already, return url)
  getCanonicalUrl: (url) => {
    const matchingDraft = state.drafts.find((draft) =>
      draft.shortLinkLongLinkMap.has(url)
    );
    if (matchingDraft) return matchingDraft.shortLinkLongLinkMap.get(url);

    return url;
  },

  // scheduledAt and isPinnedToSlot are the same across all drafts
  getScheduledAt: () => state.drafts[0].scheduledAt,
  getEnabledDraftScheduledAt: () => {
    const enabledDrafts = ComposerStore.getEnabledDrafts();
    return enabledDrafts[0]?.scheduledAt;
  },
  isSentPost: () => {
    const { sentPost } = AppStore.getOptions();
    return sentPost;
  },
  isPinnedToSlot: () => state.drafts[0].isPinnedToSlot,

  getMeta: () => state.meta,

  getSuggestedMediaForDraft: (draft, shouldFilterOutAttachedMedia) => {
    const enabledDrafts = ComposerStore.getEnabledDrafts();
    const draftsSharedData = ComposerStore.getDraftsSharedData();

    const otherEnabledDrafts = enabledDrafts.filter(
      (enabledDraft) => enabledDraft.id !== draft.id
    );
    const { availableImages } = draft;
    const availableLinkThumbnails =
      draft.link !== null && draft.link.availableThumbnails !== null
        ? draft.link.availableThumbnails
        : [];
    const availableSourceLinkImages =
      draft.sourceLink !== null ? draft.sourceLink.availableImages : [];

    let suggestedMedia = Array.prototype.concat.call(
      // Other enabled drafts' attached videos
      otherEnabledDrafts.reduce(
        (attachedVideos, otherDraft) =>
          otherDraft.video !== null
            ? attachedVideos.concat(otherDraft.video)
            : attachedVideos,
        []
      ),
      // Other enabled drafts' attached images
      ...otherEnabledDrafts.map((otherDraft) => otherDraft.images),
      // Other enabled drafts' attached gifs (if service supports gifs)

      draft.service.canHaveMediaAttachmentType(MediaTypes.GIF)
        ? otherEnabledDrafts.reduce(
            (attachedGifs, otherDraft) =>
              otherDraft.gif !== null
                ? attachedGifs.concat(otherDraft.gif)
                : attachedGifs,
            []
          )
        : [],
      draftsSharedData.uploadedVideos, // All uploaded videos
      draftsSharedData.uploadedImages, // All uploaded images
      // All uploaded gifs (if service supports gifs)
      draft.service.canHaveMediaAttachmentType(MediaTypes.GIF)
        ? draftsSharedData.uploadedGifs
        : [],
      availableImages, // This draft's available images
      availableLinkThumbnails, // This draft's link attachment's available thumbnails
      availableSourceLinkImages // Images found on this draft's source url page
    );

    if (shouldFilterOutAttachedMedia) {
      suggestedMedia = suggestedMedia.filter(
        (suggestedItem) =>
          !draft.images.find((image) => suggestedItem.url === image.url) &&
          !(draft.video && draft.video.url === suggestedItem.url) &&
          !(draft.gif && draft.gif.url === suggestedItem.url)
      );

      // Don't suggest media for TikTok or Instagram Reels
      if (draft.isVerticalVideoOnlySupported()) {
        suggestedMedia = [];
      }

      if (draft.hasThread()) {
        const allThreadedMedia = draft.thread.reduce(
          (threadedMedia, threadedDraft) => {
            if (threadedDraft.video) {
              threadedMedia.push(threadedDraft.video.url);
            }
            if (threadedDraft.gif) {
              threadedMedia.push(threadedDraft.gif.url);
            }
            if (
              Array.isArray(threadedDraft.images) &&
              threadedDraft.images.length > 0
            ) {
              threadedMedia.push(...threadedDraft.images.map((img) => img.url));
            }

            return threadedMedia;
          },
          []
        );
        suggestedMedia = suggestedMedia.filter(
          (suggestedItem) =>
            !allThreadedMedia.find((url) => suggestedItem.url === url)
        );
      }
    }

    // Deduplicate suggested media
    return uniqBy(suggestedMedia, (e) => e.url);
  },

  /**
   * Get the first selected profile for link shortenig config
   *  If omni, use the first selected profile
   *  or first connected profile if no profiles are selected
   *
   * @param { string } draftId
   * @returns {Profile | undefined}
   */
  getFirstSelectedProfile: (draftId) => {
    const draft = ComposerStore.getDraft(draftId);
    let profiles = [];
    if (draft?.service.isOmni) {
      const enabledDrafts = ComposerStore.getEnabledDrafts();
      profiles =
        enabledDrafts.length > 0
          ? AppStore.getSelectedProfilesForService(
              enabledDrafts[0].service.name
            )
          : AppStore.getProfiles();
    } else {
      profiles = draft?.isEnabled
        ? AppStore.getSelectedProfilesForService(draft.service.name)
        : [];
    }

    return profiles.length === 0 ? undefined : profiles[0];
  },

  getActiveThreadId: () => state.meta.activeThreadId,

  /**
   * Do not use: synchronous "dispatches" go against Flux's ideas, this method
   * is only used in a single place to prevent a race condition.
   */
  // eslint-disable-next-line no-use-before-define
  _syncDispatch: (payload) => onDispatchedPayload(payload),
};

// Should be used for composition with functions that are passed a draft id as first argument.
// It would have been awesome to compose this as a decorator on top of non-class methods,
// alas this isn't part of the spec yet.
const monitorComposerLastInteractedWith =
  (fn) =>
  (draftId, ...restArgs) => {
    if (
      draftId === 'omni' ||
      draftId === AppStore.getAppState().expandedComposerId
    ) {
      state.meta.lastInteractedWithComposerId = draftId;
    }

    return fn(draftId, ...restArgs);
  };

const updateDraftHasSavingError = (id, hasSavingError) => {
  ComposerStore.getDraft(id).hasSavingError = hasSavingError;
};

const clearDraftInlineErrors = (id) => {
  const notifications = NotificationStore.getVisibleNotifications().filter(
    (notif) =>
      notif.scope ===
        `${NotificationScopes.UPDATE_SAVING}-${ErrorTypes.INLINE}-${id}` ||
      notif.scope === `${NotificationScopes.PROFILE_QUEUE_LIMIT}-${id}`
  );
  notifications.forEach((notif) =>
    NotificationActionCreators.removeNotification(notif.id)
  );
};

const enableDraft = monitorComposerLastInteractedWith(
  (id, markAppAsLoadedWhenDone) => {
    let shouldPreventRerender = false;

    if (ComposerStore.isDraftLocked(id)) return !shouldPreventRerender;

    /**
     * Dashboard-specific rules:
     * - If omnibox is disabled, and there's only a single and empty composer currently
     *   enabled, then enabling a new draft will enable the omnibox, effectively keeping
     *   a single composer visible (the omnibox's)
     * - If omnibox is disabled, and there are multiple or non-empty composers currently
     *   enabled, then enabling a new draft will show an additional composer, which will
     *   be prefilled with the previous composer's contents
     */
    const { appEnvironment } = AppStore.getMetaData();
    const isDashboardEnv =
      appEnvironment === AppEnvironments.WEB_DASHBOARD ||
      appEnvironment === AppEnvironments.CALENDAR ||
      appEnvironment === AppEnvironments.CAMPAIGNS;

    if (isDashboardEnv) {
      const { isOmniboxEnabled } = AppStore.getAppState();

      if (!isOmniboxEnabled) {
        const enabledDrafts = ComposerStore.getEnabledDrafts();
        const hasOneEmptyEnabledDraft =
          enabledDrafts.length === 1 && enabledDrafts[0].isEmpty();

        if (hasOneEmptyEnabledDraft) {
          AppActionCreators.updateOmniboxState(true);
          shouldPreventRerender = true;
        } else if (enabledDrafts.length >= 1) {
          const draftFrom = ComposerStore.getDraft(
            state.meta.lastInteractedWithComposerId
          );
          // if it's a Thread, we want to preserve editor state to thread structure
          if (draftFrom && draftFrom.hasThread()) {
            draftFrom.thread[state.meta.activeThreadId] =
              getThreadedDraftData(draftFrom);
          }
          copyDraftContents({
            draftFrom,
            draftsTo: [ComposerStore.getDraft(id)],
          });
        }
      }
    }

    ComposerStore.getDraft(id).isEnabled = true;
    ComposerActionCreators.updateInstagramState();
    if (markAppAsLoadedWhenDone) AppActionCreators.markAppAsLoaded();

    return !shouldPreventRerender;
  }
);

const disableDraft = (id) => {
  let shouldPreventRerender = false;

  if (ComposerStore.isDraftLocked(id)) return !shouldPreventRerender;
  ComposerStore.getDraft(id).isEnabled = false;

  // Disable omnibox mode if needed
  const { isOmniboxEnabled } = AppStore.getAppState();
  const enabledDrafts = ComposerStore.getEnabledDrafts();
  const hasOneEnabledDraft = enabledDrafts.length === 1;

  if (isOmniboxEnabled && hasOneEnabledDraft) {
    AppActionCreators.updateOmniboxState(null);
    copyDraftContents({ draftsTo: enabledDrafts });
    shouldPreventRerender = true;
  }

  NotificationActionCreators.removeComposerOmniboxNotices(id);

  return !shouldPreventRerender;
};

const getFirstNonNullOrUndefined = (...values) =>
  values.find((v) => typeof v !== 'undefined' && v !== null);

const setDraftInitialText = ({
  id,
  text,
  url,
  facebookMentionEntities,
  via,
  composerInitiator,
  isPrefillingExistingUpdate,
  isEditing,
  annotations,
}) => {
  const draft = ComposerStore.getDraft(id);

  if (draft.enabledAttachmentType === AttachmentTypes.RETWEET) {
    const retweetMark = 'RT @';

    if (text.includes(retweetMark)) {
      text = text.substring(0, text.indexOf(` ${retweetMark}`));
      const hasRetweetComment = text?.length !== 0;
      if (!hasRetweetComment) return;
    }
  }

  const shouldAppendUrl =
    draft.service.name !== 'pinterest' &&
    draft.service.name !== 'mastodon' &&
    (draft.service.name !== 'instagram' ||
      !ComposerInitiators.ImageBufferButtons.includes(composerInitiator));

  const initialText = [
    text,
    shouldAppendUrl ? url : null,
    via ? `via @${via}` : null,
  ]
    .filter((val) => val)
    .join(' ');

  /**
   * willGetOverridenByOmnibox is a patch that serves to prevent the Facebook
   * text-prefilling notification below from showing up when MC starts with the
   * omnibox and other composers still get prefilled on load (hence queueing that
   * tooltip on load, despite the fact that the omnibox's contents will override
   * what was prefilled into Facebook anyway, making the tooltip unnecessary).
   * TODO: A better way of doing this will be to refactor how MC loads -> currently
   * all drafts are prefilled on load (omnibox and social networks); instead we'll
   * want only the omnibox to be prefilled, or only the social networks to be
   * prefilled if the omnibox isn't enabled. It'll be less wasteful, and will
   * remove the need for patches like willGetOverridenByOmnibox which should ideally
   * not exist. Most (if not all) actions created from loadInitialMetaData() will
   * need to be refactored to only target either the omnibox or the other drafts.
   */
  const willGetOverridenByOmnibox =
    AppStore.getAppState().isOmniboxEnabled && !draft.service.isOmni;

  if (
    draft.service.doesRequireHumanInteractionToUpdateText &&
    !isPrefillingExistingUpdate &&
    !willGetOverridenByOmnibox
  ) {
    NotificationActionCreators.queueInfo({
      scope: `${NotificationScopes.COMPOSER_NOTICE_NOT_PREFILLED}-${draft.service.name}`,
      message: `<a href="https://support.buffer.com/hc/en-us/articles/360038455374-Why-is-it-necessary-to-manually-enter-a-message-for-Facebook-in-certain-places-?${HC_UTM_PARAMS}" className={styles.composerInfoMessageLink}
                target="_blank" rel="noopener noreferrer">Why is it necessary to manually
                enter a message for Facebook?</a>`,
      onlyCloseOnce: true,
    });
    return;
  }

  /**
   * Show a message warning users about posting duplicate content on Twitter
   */
  if (
    draft.service.shouldShowDuplicateContentWarning &&
    isPrefillingExistingUpdate &&
    !isEditing
  ) {
    NotificationActionCreators.queueInfo({
      scope: NotificationScopes.TWITTER_DUPLICATE_CONTENT_WARNING,
      message: `Twitter does not allow posting multiple tweets that are “substantially similar”. Please make sure to modify your tweet before scheduling to avoid risking account suspension! <a href="https://support.buffer.com/hc/en-us/articles/360037727174-Limitations-with-sharing-duplicate-content-on-Twitter?${HC_UTM_PARAMS}" className={styles.composerInfoMessageLink}
                target="_blank" rel="noopener noreferrer">Learn more</a>`,
    });
  }

  const newEditorState = EditorStateProxy.createStateFromText(
    draft.editorState,
    initialText,
    draft.service.name,
    {
      annotations,
      facebookMentionEntities,
    }
  );

  draft.editorState = newEditorState;

  if (draft.service.isInstagram()) {
    ComposerActionCreators.updateDraftCommentCharacterCount(id);
  }

  if (draft.service.isPinterest()) {
    ComposerActionCreators.updateDraftTitleCharacterCount(id);
  }

  ComposerActionCreators.updateDraftCharacterCount(id);
};

const setDraftsInitialText = ({
  text,
  url,
  facebookMentionEntities,
  via,
  composerInitiator,
  isPrefillingExistingUpdate,
  isEditing,
  annotations,
}) => {
  state.drafts.forEach((draft) =>
    setDraftInitialText({
      id: draft.id,
      text,
      url,
      facebookMentionEntities,
      via,
      composerInitiator,
      isPrefillingExistingUpdate,
      isEditing,
      annotations,
    })
  );
};

const setDraftEditorState = monitorComposerLastInteractedWith(
  (id, editorState) => {
    ComposerStore.getDraft(id).editorState = editorState;
    ComposerActionCreators.updateDraftCharacterCount(id);
    ComposerActionCreators.updateDraftCommentCharacterCount(id);
  }
);

const updateDraftIsSaved = (id) => {
  ComposerStore.getDraft(id).isSaved = true;
};

const debouncedUpdateDraftSourceLinkDataActionCreator = debounce((id, url) => {
  ComposerActionCreators.updateDraftSourceLinkData(id, url);
}, 250);

const updateDraftSourceLink = monitorComposerLastInteractedWith((id, url) => {
  const draft = ComposerStore.getDraft(id);
  if (!draft.service.canHaveSourceUrl) return;

  if (url === '' || url === null) {
    draft.sourceLink = null;
  } else {
    draft.sourceLink = getNewSourceLink(url);

    const isValidUrl = twitterText.isValidUrl(url, true, false);
    if (isValidUrl) debouncedUpdateDraftSourceLinkDataActionCreator(id, url);
  }
});

const updateDraftSourceLinkData = (id, { url, images = [] }) => {
  if (!ComposerStore.doesDraftHaveSourceLink(id)) return;

  const draftSourceLink = ComposerStore.getDraft(id).sourceLink;

  Object.assign(draftSourceLink, {
    url,
    availableImages: images.map((image) =>
      getNewImage({
        url: image.url,
        width: image.width,
        height: image.height,
      })
    ),
  });
};

const updateDraftLocation = monitorComposerLastInteractedWith(
  (id, locationId, locationName) => {
    const draft = ComposerStore.getDraft(id);

    if (!draft.service.canHaveLocation) return;

    draft.locationId = locationId;
    draft.locationName = locationName;
  }
);

const updateDraftImageUserTags = monitorComposerLastInteractedWith(
  (id, userTags) => {
    const draft = ComposerStore.getDraft(id);
    // for tag case, there should only be one image
    if (draft.images && draft.images[0]) {
      draft.images[0].userTags = userTags;
    }
  }
);

const updateDraftCampaignId = monitorComposerLastInteractedWith(
  (id, campaignId) => {
    const draft = ComposerStore.getDraft(id);
    if (draft) {
      draft.campaignId = campaignId;
    }
  }
);

const updateDraftTitle = monitorComposerLastInteractedWith((id, title) => {
  const draft = ComposerStore.getDraft(id);
  if (draft) {
    draft.title = title;
  }
});

const updateDraftScheduledAt = monitorComposerLastInteractedWith(
  (id, timestamp, isPinnedToSlot = false) => {
    const draft = ComposerStore.getDraft(id);
    draft.scheduledAt = timestamp;
    draft.isPinnedToSlot = isPinnedToSlot;
  }
);

const updateDraftListPlaces = (id, places) => {
  const draft = ComposerStore.getDraft(id);
  draft.places = places;
};

const updateInstagramDraftThumbnail = (id, thumbOffset, thumbnail) => {
  const draft = ComposerStore.getDraft(id);
  if (draft.video) {
    draft.video.thumbnail = thumbnail;
    draft.video.thumbOffset = thumbOffset;
  }
};

const updateDraftThumbnailGenerated = (draftId, thumbnailURL) => {
  const draft = ComposerStore.getDraft(draftId);
  if (draft.video) {
    draft.video.thumbnail = thumbnailURL;
    draft.video.thumbOffset = 0;
  }
};

const updateDraftCharacterCount = monitorComposerLastInteractedWith(
  (id, didEditorStateChange) => {
    const draft = ComposerStore.getDraft(id);
    if (draft.service.charLimit === null) return;

    const draftText = ComposerStore.getDraftText(id);
    draft.characterCount = DraftMethods.getFullCharacterCount(draft, draftText);

    // If the character count was updated as a result of a change that didn't
    // originate from the editor itself, we need to give the editor an opportunity
    // to re-render the highlighter's decorator ourselves. A prop is used to tell
    // the editor to re-render decorators. The editor itself sets that prop back
    // to false using the draft reference.
    if (!didEditorStateChange) draft.forceDecoratorsRerender = true;
  }
);

const updateToggleSidebarVisibility = monitorComposerLastInteractedWith(
  (id, composerSidebarVisible, didEditorStateChange) => {
    let draft;
    if (id) {
      draft = ComposerStore.getDraft(id);
      draft.composerSidebarVisible = composerSidebarVisible;

      // If the character count was updated as a result of a change that didn't
      // originate from the editor itself, we need to give the editor an opportunity
      // to re-render the highlighter's decorator ourselves. A prop is used to tell
      // the editor to re-render decorators. The editor itself sets that prop back
      // to false using the draft reference.
      if (!didEditorStateChange) draft.forceDecoratorsRerender = true;
    } else {
      const enabledDrafts = ComposerStore.getEnabledDrafts();

      enabledDrafts.forEach((enabledDraft) => {
        enabledDraft.composerSidebarVisible = composerSidebarVisible;
      });
    }
  }
);

const updateTogglePostPreviewVisibility = monitorComposerLastInteractedWith(
  (id, composerPostPreviewVisible, didEditorStateChange) => {
    let draft;
    if (id) {
      draft = ComposerStore.getDraft(id);
      draft.composerPostPreviewVisible = composerPostPreviewVisible;

      // If the character count was updated as a result of a change that didn't
      // originate from the editor itself, we need to give the editor an opportunity
      // to re-render the highlighter's decorator ourselves. A prop is used to tell
      // the editor to re-render decorators. The editor itself sets that prop back
      // to false using the draft reference.
      if (!didEditorStateChange) draft.forceDecoratorsRerender = true;
    } else {
      const enabledDrafts = ComposerStore.getEnabledDrafts();

      enabledDrafts.forEach((enabledDraft) => {
        enabledDraft.composerPostPreviewVisible = composerPostPreviewVisible;
      });
    }
  }
);

const hideSuggestedMedia = () => {
  state.meta.isSuggestedMediaHidden = true;
};

const updateDraftComment = monitorComposerLastInteractedWith(
  (id, commentText, didEditorStateChange) => {
    let draft;
    if (id) {
      draft = ComposerStore.getDraft(id);
      if (draft.service.name !== 'instagram') return;
      draft.commentText = commentText;

      // If the character count was updated as a result of a change that didn't
      // originate from the editor itself, we need to give the editor an opportunity
      // to re-render the highlighter's decorator ourselves. A prop is used to tell
      // the editor to re-render decorators. The editor itself sets that prop back
      // to false using the draft reference.
      if (!didEditorStateChange) draft.forceDecoratorsRerender = true;
    } else {
      const enabledDrafts = ComposerStore.getEnabledDrafts();

      enabledDrafts.forEach((enabledDraft) => {
        const currentText = enabledDraft.commentText
          ? `${enabledDraft.commentText} `
          : '';
        const comment = `${currentText}${commentText}`;
        enabledDraft.commentText = comment;
      });
    }
  }
);

const updateDraftChannelData = monitorComposerLastInteractedWith(
  (id, channelData, didEditorStateChange) => {
    const draft = ComposerStore.getDraft(id);

    draft.setChannelData(channelData);

    if (!didEditorStateChange) {
      draft.forceDecoratorsRerender = true;
    }
  }
);

const updateShopgridLink = monitorComposerLastInteractedWith(
  (id, shopgridLink, didEditorStateChange) => {
    const draft = ComposerStore.getDraft(id);
    if (draft.service.name !== 'instagram') return;
    draft.shopgridLink = shopgridLink;

    if (!didEditorStateChange) draft.forceDecoratorsRerender = true;
  }
);

const updateDraftUpdateType = monitorComposerLastInteractedWith(
  (id, updateType, didEditorStateChange) => {
    const draft = ComposerStore.getDraft(id);
    draft.updateType = updateType;

    const isSingleMediaPostType =
      updateType === PostTypeStory || updateType === PostTypeReel;

    if (draft.service.isInstagram() && isSingleMediaPostType) {
      draft.service.maxAttachableImagesCount = 1;
    }
    if (draft.service.isInstagram() && !isSingleMediaPostType) {
      draft.service.maxAttachableImagesCount = 10;
    }

    if (!didEditorStateChange) draft.forceDecoratorsRerender = true;
  }
);

const updateDraftCommentCharacterCount = monitorComposerLastInteractedWith(
  (id, didEditorStateChange) => {
    let draft;
    if (id) {
      draft = ComposerStore.getDraft(id);
      if (draft.service.name !== 'instagram') return;
      if (draft.service.commentCharLimit === null) return;

      draft.characterCommentCount = DraftMethods.getFullCharacterCount(
        draft,
        draft.commentText
      );

      // If the character count was updated as a result of a change that didn't
      // originate from the editor itself, we need to give the editor an opportunity
      // to re-render the highlighter's decorator ourselves. A prop is used to tell
      // the editor to re-render decorators. The editor itself sets that prop back
      // to false using the draft reference.
      if (!didEditorStateChange) draft.forceDecoratorsRerender = true;
    } else {
      const enabledDrafts = ComposerStore.getEnabledDrafts();

      enabledDrafts.forEach((enabledDraft) => {
        if (enabledDraft.service.commentCharLimit === null) return;
        enabledDraft.characterCommentCount = DraftMethods.getFullCharacterCount(
          enabledDraft,
          enabledDraft.commentText
        );

        if (!didEditorStateChange) enabledDraft.forceDecoratorsRerender = true;
      });
    }
  }
);

const updateDraftTitleCharacterCount = monitorComposerLastInteractedWith(
  (id, didEditorStateChange) => {
    let draft;
    if (id) {
      draft = ComposerStore.getDraft(id);
      if (draft.service.name !== 'pinterest') return;
      if (draft.service.titleCharLimit === null) return;

      draft.characterTitleCount = DraftMethods.getFullCharacterCount(
        draft,
        draft.title
      );

      // If the character count was updated as a result of a change that didn't
      // originate from the editor itself, we need to give the editor an opportunity
      // to re-render the highlighter's decorator ourselves. A prop is used to tell
      // the editor to re-render decorators. The editor itself sets that prop back
      // to false using the draft reference.
      if (!didEditorStateChange) draft.forceDecoratorsRerender = true;
    } else {
      const enabledDrafts = ComposerStore.getEnabledDrafts();

      enabledDrafts.forEach((enabledDraft) => {
        if (enabledDraft.service.titleCharLimit === null) return;
        enabledDraft.characterTitleCount = DraftMethods.getFullCharacterCount(
          enabledDraft,
          enabledDraft.title
        );

        if (!didEditorStateChange) enabledDraft.forceDecoratorsRerender = true;
      });
    }
  }
);

const mapShortLinkWithLongLink = (id, shortLink, longLink) => {
  const draft = ComposerStore.getDraft(id);
  draft.shortLinkLongLinkMap.set(shortLink, longLink);
};

const unmapShortLinkWithLongLink = (id, link) => {
  const draft = ComposerStore.getDraft(id);
  draft.shortLinkLongLinkMap.delete(link);
};

/**
 * Handle all new LinkedIn entity urls that should be converted
 * to annotations.
 *
 * If multiple LinkedIn profiles are selected, we use the id from
 * the first one for the LinkedIn lookup requests.
 *
 * This function is run async as not to block the rest of the editor,
 * but inside it we wait for all lookup requests to complete
 * before starting to edit the draft editor state.
 *
 * We do this because if we start the editing flow while the
 * requests aren't completed yet, we risk ending up with a stale
 * state by the time we push our updates, which would overwrite
 * any changes made to the editor state while we were waiting
 * for the requests to complete.
 */
const handleNewLinkedInEntityUrls = async (newUrls) => {
  const draftId = Service.Linkedin;
  const [profile] = AppStore.getSelectedProfilesForService(draftId);

  if (profile) {
    const entityUrls = await LinkedInAnnotation.parseLinkedInEntitiesUrlInText(
      newUrls,
      profile.id
    );

    let newEditorState = ComposerStore.getDraft(draftId)?.editorState;
    if (entityUrls.length !== 0) {
      entityUrls.forEach((entityDetails) => {
        newEditorState = EditorStateProxy.insertLinkedInAnnotation(
          newEditorState,
          entityDetails
        );
      });
      // At the end, run setDraftEditorState to perform other actions
      setDraftEditorState(draftId, newEditorState);
    }
  }
};

/**
 * Loop over each block of text in the editor, and update the list of all urls
 * within the composer + detect new and removed ones
 */
const parseDraftTextLinks = (id) => {
  const draft = ComposerStore.getDraft(id);
  const parsedUrls = EditorStateProxy.parseLinks(draft.editorState);

  const newUrls = parsedUrls.filter((url) => !draft.urls.includes(url));
  const removedUrls = draft.urls.filter((url) => !parsedUrls.includes(url));

  draft.urls = parsedUrls;

  if (newUrls.length) handleNewDraftLinks(id, newUrls);
  if (removedUrls.length) handleRemovedDraftLinks(id, removedUrls);

  if (newUrls.length || removedUrls.length)
    ComposerActionCreators.updateDraftCharacterCount(id);
};

/**
 * Used to display notices for links and mentions. We should remove it when we implement
 * autocomplete plugins for Mastodon.
 */
const parseMastodonText = () => {
  const draft = ComposerStore.getDraft('mastodon');
  const text = EditorStateProxy.getPlainText(draft.editorState);
  const hasMentionsWithoutServer =
    MastodonParser.hasMentionsWithoutServer(text);
  state.meta.hasMentionsWithoutServer = hasMentionsWithoutServer;
};

const handleNewDraftLinks = (id, newUrls) => {
  const draft = ComposerStore.getDraft(id);

  const isLinkedInDraft = draft.service.name === Service.Linkedin;
  if (isLinkedInDraft) {
    handleNewLinkedInEntityUrls(newUrls);
    // LinkedIn entity urls are already processed
    newUrls = newUrls.filter(
      (url) => !LinkedInAnnotation.isLinkedInEntityUrl(url)
    );
  }

  if (newUrls.length === 0) return;

  // If link attachment is disabled update it with first newly-added url
  // (As long as it's not on the blocklist)
  const isBlocked = isUrlOnBlocklist(ComposerStore.getCanonicalUrl(newUrls[0]));
  if (!isBlocked) {
    // If draft does not have any active link attachments
    // process links for attachments.
    if (!ComposerStore.doesDraftHaveLinkAttachmentEnabled(id)) {
      ComposerActionCreators.updateDraftLink(id, newUrls[0]);

      // If no attachment is currently added, bring link attachment up with new link
      if (!draft.hasAttachment()) {
        ComposerActionCreators.toggleAttachment(id, AttachmentTypes.LINK);
      }
    }
  } else {
    // If the first URL is blocked, show a notice about blocked URLs.
    NotificationActionCreators.queueInfo({
      scope: NotificationScopes.FB_IG_URL_NO_LINK_PREVIEW,
      message: `Due to recent changes with Facebook’s Data Policy, we can no longer generate link previews for <br />
      Facebook and Instagram links.
      <a
        rel="noopener noreferrer"
        target="_blank"
        href="https://support.buffer.com/hc/en-us/articles/1500002337761?${HC_UTM_PARAMS}">
        Learn more
      </a>.`,
      isUnique: true,
      onlyCloseOnce: true,
    });
  }

  newUrls.forEach((newUrl) => {
    // Shorten newly-added links if not unshortened before
    const wasUnshortenedBefore = draft.unshortenedUrls.includes(newUrl);
    if (!wasUnshortenedBefore)
      shortenDraftLink(id, newUrl, ComposerStore.getFirstSelectedProfile(id));

    // Add images scraped from new links to list of available images
    ComposerActionCreators.updateDraftLinkAvailableImages(id, newUrl);
  });
};

const handleRemovedDraftLinks = (id, removedUrls) => {
  const draft = ComposerStore.getDraft(id);

  removedUrls.forEach((removedUrl) => {
    // Remove from available images those whose source link has been removed
    ComposerActionCreators.removeDraftLinkAvailableImages(id, removedUrl);

    // Forget this link was possibly unshortened before to allow shortening it
    // again if re-added
    const unshortenedUrlIndex = draft.unshortenedUrls.indexOf(removedUrl);
    if (unshortenedUrlIndex !== -1)
      draft.unshortenedUrls.splice(unshortenedUrlIndex, 1);

    unmapShortLinkWithLongLink(id, removedUrl);
  });
};

/**
 * One-stop shop for updating draft link data. When a new link attachment is created,
 * if a piece of link attachment data that could be retrieved from the scraper is
 * missing, the scraper will be automatically invoked to retrieve more data.
 *
 * @param {string} id - Draft id
 * @param {object} linkData - Contains any number of link data properties
 * @param {object} meta
 * @param {boolean} meta.isNewLinkAttachment - Whether a new link is being attached,
 *                                             or an existing one being updated
 * @param {boolean} meta.comesFromDirectUserAction - Whether e.g. a keypress led to
 *                                                   linkData being updated
 *
 * The values from both meta.isNewLinkAttachment and meta.comesFromDirectUserAction
 * will determine how linkData is used. If meta.isNewLinkAttachment is true, linkData
 * will replace all previous values, and missing data will be reset to null. If it's
 * false, then linkData will be composed onto the existing link attachment's data.
 * Some properties can be customized by users and are valuable to keep around when new
 * link attachement data is set (e.g. after scraping a link). That's the case of
 * link.title, link.description, and link.thumbnail. When meta.comesFromDirectUserAction
 * is false, these properties will never be overridden by new linkData. When it's true,
 * these properties will always take new values from new linkData (if provided).
 *
 * Example usage:
 *
 * 1. Creating a new empty link attachment:
 *
 *    updateDraftLinkData(id, { url }, {
 *      isNewLinkAttachment: true,
 *      comesFromDirectUserAction: false,
 *    })
 *
 * 2. Updating a link attachment's title when users manually edit it:
 *
 *    updateDraftLinkData(id, { title }, {
 *      isNewLinkAttachment: false,
 *      comesFromDirectUserAction: true,
 *    })
 *
 * 3. Update a link attachment's data after scraping the page:
 *
 *    updateDraftLinkData(id, scrapedData, {
 *      isNewLinkAttachment: false,
 *      comesFromDirectUserAction: false,
 *    })
 */
const updateDraftLinkData = monitorComposerLastInteractedWith(
  (id, linkData, meta) => {
    const { isNewLinkAttachment, comesFromDirectUserAction } = meta;
    const draft = ComposerStore.getDraft(id);

    const {
      url = null,
      expandedUrl = null,
      title = null,
      description = null,
      thumbnail = null,
      availableThumbnails = null,
      originalThumbnailUrl = null,
    } = linkData;

    if (!draft.service.canHaveAttachmentType(AttachmentTypes.LINK)) return;

    const hasLinkAttachment = draft.link !== null;
    const hasAvailableThumbnails =
      availableThumbnails !== null && availableThumbnails.length > 0;
    const fallBackToLinkAttachmentData =
      !isNewLinkAttachment && hasLinkAttachment;
    const prioritizeLinkAttachmentData =
      fallBackToLinkAttachmentData && !comesFromDirectUserAction;

    // In some situations (e.g. editing an update that has a custom thumbnail in the dashboard),
    // an attached thumbnail won't be found among the scraped availableThumbnails: this flag is used
    // to know when to insert such an attached thumbnail into the collection of availableThumbnails
    // so that navigating between available thumbnails doesn't lose this initial thumbnail.
    const hasUnavailableThumbnailAttached =
      draft.link !== null &&
      draft.link.thumbnail !== null &&
      availableThumbnails !== null &&
      !availableThumbnails.some(
        (thumb) => thumb.url === draft.link.thumbnail.url
      );

    let linkThumbnail = null;
    if (draft.link !== null && draft.link.thumbnail !== null) {
      linkThumbnail = originalThumbnailUrl
        ? { ...draft.link.thumbnail, originalUrl: originalThumbnailUrl }
        : draft.link.thumbnail;
    }

    draft.link = getNewLink({
      url: getFirstNonNullOrUndefined(
        url,
        fallBackToLinkAttachmentData ? draft.link.url : null
      ),
      expandedUrl: getFirstNonNullOrUndefined(
        expandedUrl,
        fallBackToLinkAttachmentData ? draft.link.expandedUrl : null
      ),
      title: getFirstNonNullOrUndefined(
        prioritizeLinkAttachmentData ? draft.link.title : null,
        title !== null ? title.replace(/\n/g, '') : null, // Strip out line breaks
        fallBackToLinkAttachmentData ? draft.link.title : null
      ),
      description: getFirstNonNullOrUndefined(
        prioritizeLinkAttachmentData ? draft.link.description : null,
        description,
        fallBackToLinkAttachmentData ? draft.link.description : null
      ),
      thumbnail: getFirstNonNullOrUndefined(
        thumbnail !== null
          ? getNewImage({
              url: thumbnail,
              originalUrl: thumbnail.originalUrl,
            })
          : null,
        fallBackToLinkAttachmentData ? linkThumbnail : null,
        hasAvailableThumbnails
          ? getNewImage({
              url: availableThumbnails[0].url,
              originalUrl: availableThumbnails[0].url,
              width: availableThumbnails[0].width,
              height: availableThumbnails[0].height,
            })
          : null
      ),
      thumbnailHttps: null,
      availableThumbnails: getFirstNonNullOrUndefined(
        availableThumbnails !== null
          ? [
              ...(hasUnavailableThumbnailAttached
                ? [draft.link.thumbnail]
                : []),
              ...availableThumbnails.map((thumb) =>
                getNewImage({
                  url: thumb.url,
                  width: thumb.width,
                  height: thumb.height,
                })
              ),
            ]
          : null,
        fallBackToLinkAttachmentData ? draft.link.availableThumbnails : null
      ),
      wasEdited: isNewLinkAttachment
        ? false
        : comesFromDirectUserAction || !!(draft.link && draft.link.wasEdited),
    });

    // If the link is missing some data that we could retrieve by scraping it, do so
    const scrapableProps = ['title', 'description', 'availableThumbnails'];
    const isAnyScrapablePropMissing = scrapableProps.some(
      (prop) => draft.link[prop] === null
    );
    if (isAnyScrapablePropMissing)
      ComposerActionCreators.scrapeDraftLinkData(id, url, originalThumbnailUrl);

    if (isNewLinkAttachment)
      AppActionCreators.refreshFacebookDomainOwnershipData();
  }
);

const updateDraftFileUploadProgress = (id, uploaderInstance, progress) => {
  const { filesUploadProgress } = ComposerStore.getDraft(id);

  if (progress !== null) filesUploadProgress.set(uploaderInstance, progress);
  else filesUploadProgress.delete(uploaderInstance);
};

const completeUploadProgress = (id, uploaderInstance) => {
  return updateDraftFileUploadProgress(id, uploaderInstance, null);
};

const addDraftImage = monitorComposerLastInteractedWith((id, image) => {
  const draft = ComposerStore.getDraft(id);
  const hasAttachedVideo = draft.video !== null;
  const hasAttachedGif = draft.gif != null;

  const currentlyUploadingImagesCount = draft.filesUploadProgress.size;
  // + 1 to ensure we take into account current image we're adding
  const newImagesOverflow =
    draft.images.length +
    currentlyUploadingImagesCount -
    draft.service.maxAttachableImagesCount +
    1;
  const needsToMakeRoomForNewImages = newImagesOverflow > 0;

  if (!draft.service.canHaveAttachmentType(AttachmentTypes.MEDIA)) return;
  if (!draft.service.canHaveMediaAttachmentType(MediaTypes.IMAGE)) return;

  if (needsToMakeRoomForNewImages)
    draft.images.splice(-newImagesOverflow, newImagesOverflow);
  if (hasAttachedVideo) draft.video = null; // Override video
  if (hasAttachedGif) draft.gif = null; // Override gif

  // If the image comes from Canva we are replacing any existing image
  // so that you get the feeling of editing the image
  const prevImage = shouldReplaceCanvaImage(image, draft.images);
  if (prevImage) {
    const index = draft.images.indexOf(prevImage);
    draft.images[index] = image;
  } else {
    draft.images.push(image);
  }

  ComposerActionCreators.draftImageAdded(id, image.url);
  ComposerActionCreators.updateDraftCharacterCount(id, {
    didEditorStateChange: false,
  });
  if (draft.id === 'instagram') ComposerActionCreators.updateInstagramState();
});

const updateDraftVideoThumbnail = monitorComposerLastInteractedWith(
  (id, thumbnail) => {
    const draft = ComposerStore.getDraft(id);
    if (!draft.service.canEditVideoAttachment) return;

    draft.video.thumbnail = thumbnail;
    draft.video.wasEdited = true;
  }
);

const updateDraftVideoTitle = monitorComposerLastInteractedWith((id, title) => {
  const draft = ComposerStore.getDraft(id);
  if (!draft.service.canEditVideoAttachment && !draft.service.canEditVideoTitle)
    return;

  draft.video.name = title;
  draft.video.wasEdited = true;
});

const updateDraftLinkThumbnail = monitorComposerLastInteractedWith(
  (id, thumbnail) => {
    const draft = ComposerStore.getDraft(id);
    draft.link.thumbnail = thumbnail;
    draft.link.thumbnailHttps = thumbnail.url;

    // TODO: merge updateDraftLinkThumbnail() into updateDraftLinkData()
    draft.link.wasEdited = true;
  }
);

const selectNextLinkThumbnail = monitorComposerLastInteractedWith((draftId) => {
  const draft = ComposerStore.getDraft(draftId);
  const { availableThumbnails } = draft.link;
  const currThumbnailIndex = findLastIndexOf(
    availableThumbnails,
    (thumbnail) => thumbnail.url === draft.link.thumbnail.url
  );
  let nextThumbnail;

  if (currThumbnailIndex === availableThumbnails.length - 1) {
    // eslint-disable-next-line prefer-destructuring
    nextThumbnail = availableThumbnails[0];
  } else {
    nextThumbnail = availableThumbnails[currThumbnailIndex + 1];
  }

  ComposerActionCreators.updateDraftLinkThumbnail(draftId, nextThumbnail);
});

const selectPreviousLinkThumbnail = monitorComposerLastInteractedWith(
  (draftId) => {
    const draft = ComposerStore.getDraft(draftId);
    const { availableThumbnails } = draft.link;
    const currThumbnailIndex = availableThumbnails.findIndex(
      (thumbnail) => thumbnail.url === draft.link.thumbnail.url
    );
    let prevThumbnail;

    if (currThumbnailIndex === 0) {
      prevThumbnail = availableThumbnails[availableThumbnails.length - 1];
    } else {
      prevThumbnail = availableThumbnails[currThumbnailIndex - 1];
    }

    ComposerActionCreators.updateDraftLinkThumbnail(draftId, prevThumbnail);
  }
);

const addDraftLinkAvailableThumbnail = (id, thumbnail) => {
  const draft = ComposerStore.getDraft(id);
  if (draft.link.availableThumbnails === null)
    draft.link.availableThumbnails = [];
  draft.link.availableThumbnails.unshift(thumbnail);
};

const addDraftUploadedLinkThumbnail = (id, url, width, height, source) => {
  const draftsSharedData = ComposerStore.getDraftsSharedData();
  const formattedImage = getNewImage({ url, width, height, source });

  /**
   * It's important for the three collections below to share the same formattedImage
   * reference, so that a change made in one location propagates to all other places
   * seamlessly.
   * TODO: Re-implement this logic in an immutable fashion: it'll be a bit more work
   * to manually search and update all places where an image can be stored, but it'll
   * be much more solid.
   */
  updateDraftLinkThumbnail(id, formattedImage);
  addDraftLinkAvailableThumbnail(id, formattedImage);
  draftsSharedData.uploadedImages.push(formattedImage);
  state.meta.isSuggestedMediaHidden = false;
};

const shouldReplaceCanvaImage = (image, collection) => {
  if (image.source?.name === 'canva') {
    return collection.find((item) => item.source?.id === image.source.id);
  }
  return false;
};

const addDraftUploadedImage = (draftId, url, width, height, source) => {
  const draftsSharedData = ComposerStore.getDraftsSharedData();
  const formattedImage = getNewImage({ url, width, height, source });

  /**
   * It's important for the two collections below to share the same formattedImage
   * reference, so that a change made in one location propagates to all other places
   * seamlessly.
   * TODO: Re-implement this logic in an immutable fashion: it'll be a bit more work
   * to manually search and update all places where an image can be stored, but it'll
   * be much more solid.
   */

  // If the image comes from Canva we are replacing any existing image
  // so that you get the feeling of editing the image
  const prevImage = shouldReplaceCanvaImage(
    formattedImage,
    draftsSharedData.uploadedImages
  );
  if (prevImage) {
    const index = draftsSharedData.uploadedImages.indexOf(prevImage);
    draftsSharedData.uploadedImages[index] = formattedImage;
  } else {
    draftsSharedData.uploadedImages.push(formattedImage);
  }
  addDraftImage(draftId, formattedImage);
};

const addAutoUploadedImage = (url, altText, source, height, width) => {
  const draftsSharedData = ComposerStore.getDraftsSharedData();
  const formattedImage = getNewImage({ url, altText, source, height, width });

  /**
   * It's important for the two collections below to share the same formattedImage
   * reference, so that a change made in one location propagates to all other places
   * seamlessly.
   * TODO: Re-implement this logic in an immutable fashion: it'll be a bit more work
   * to manually search and update all places where an image can be stored, but it'll
   * be much more solid.
   */
  draftsSharedData.uploadedImages.push(formattedImage);
  state.drafts.forEach((draft) => addDraftImage(draft.id, formattedImage));
};

const removeDraftImage = monitorComposerLastInteractedWith((id, image) => {
  const draft = ComposerStore.getDraft(id);
  const imageIndex = draft.images.indexOf(image);

  draft.images.splice(imageIndex, 1);

  ComposerActionCreators.updateDraftCharacterCount(id, {
    didEditorStateChange: false,
  });
  if (draft.id === 'instagram') ComposerActionCreators.updateInstagramState();
});

// TODO: Refactor to not rely on reference mutations
const updateImageAltText = (image, altText) => {
  image.altText = altText;
};

/**
 * TODO: Refactor to not rely on reference mutations
 * This method is currently only called when auto-uploading images (i.e. auto-attached
 * images on app load), so the two collections where we're sure to be able to find
 * them at any point in time are draftsSharedData.uploadedImages/uploadedGifs.
 * Find the image there and mutate it. This is quite brittle, hence the todo above.
 */
const updateUploadedImageDimensions = (url, width, height) => {
  const draftsSharedData = ComposerStore.getDraftsSharedData();
  const collections = [
    draftsSharedData.uploadedImages,
    draftsSharedData.uploadedGifs,
  ];
  let image;

  collections.some((imageCollection) => {
    image = imageCollection.find((uploadedImage) => uploadedImage.url === url);
    return !!image;
  });

  if (image) {
    image.width = width;
    image.height = height;
  }
};

/**
 * Note: When adding to the draft a video that already exists within the app, it's
 * important to pass the video object's reference around rather than creating a new
 * equivalent video object, since those references are compared in different places
 * using === to establish if we're looking at the same video or not.
 */
const addDraftVideo = monitorComposerLastInteractedWith((id, video) => {
  const draft = ComposerStore.getDraft(id);
  const hasAttachedImages = draft.images?.length > 0;
  const hasAttachedVideo = draft.video !== null;
  const hasAttachedGif = draft.gif !== null;

  if (!draft.service.canHaveAttachmentType(AttachmentTypes.MEDIA)) return;
  if (!draft.service.canHaveMediaAttachmentType(MediaTypes.VIDEO)) return;

  if (hasAttachedImages) draft.images.splice(0); // Override images
  if (hasAttachedVideo) draft.video = null; // Override video
  if (hasAttachedGif) draft.gif = null;

  draft.video = cloneDeep(video);

  ComposerActionCreators.draftVideoAdded(id, video);
  ComposerActionCreators.updateDraftCharacterCount(id, {
    didEditorStateChange: false,
  });
  if (draft.id === 'instagram') ComposerActionCreators.updateInstagramState();
});

const addSharedUploadedVideo = (videoData) => {
  const draftsSharedData = ComposerStore.getDraftsSharedData();
  draftsSharedData.uploadedVideos.push(videoData);
};

const removeDraftVideo = monitorComposerLastInteractedWith((id) => {
  ComposerStore.getDraft(id).video = null;
  ComposerActionCreators.updateDraftCharacterCount(id, {
    didEditorStateChange: false,
  });
  if (id === 'instagram') ComposerActionCreators.updateInstagramState();
});

const addDraftGif = monitorComposerLastInteractedWith((id, gif) => {
  const draft = ComposerStore.getDraft(id);
  const hasAttachedImages = draft.images?.length > 0;
  const hasAttachedVideo = draft.video !== null;

  if (!draft.service.canHaveAttachmentType(AttachmentTypes.MEDIA)) return;
  if (!draft.service.canHaveMediaAttachmentType(MediaTypes.GIF)) return;

  if (hasAttachedImages) draft.images.splice(0); // Override images
  if (hasAttachedVideo) draft.video = null; // Override video
  draft.gif = gif;

  ComposerActionCreators.draftGifAdded(id, gif.url);
  ComposerActionCreators.updateDraftCharacterCount(id, {
    didEditorStateChange: false,
  });
});

const addDraftUploadedGif = (draftId, url, stillGifUrl, width, height) => {
  const draftsSharedData = ComposerStore.getDraftsSharedData();
  const formattedGif = getNewGif(url, stillGifUrl, width, height);

  /**
   * It's important for the two collections below to share the same formattedGif
   * reference, so that a change made in one location propagates to all other places
   * seamlessly.
   * TODO: Re-implement this logic in an immutable fashion: it'll be a bit more work
   * to manually search and update all places where an image can be stored, but it'll
   * be much more solid.
   */
  draftsSharedData.uploadedGifs.push(formattedGif);
  addDraftGif(draftId, formattedGif);
};

const addAutoUploadedGif = (url, stillGifUrl) => {
  const draftsSharedData = ComposerStore.getDraftsSharedData();
  const formattedGif = getNewGif(url, stillGifUrl);

  /**
   * It's important for the two collections below to share the same formattedGif
   * reference, so that a change made in one location propagates to all other places
   * seamlessly.
   * TODO: Re-implement this logic in an immutable fashion: it'll be a bit more work
   * to manually search and update all places where an image can be stored, but it'll
   * be much more solid.
   */
  draftsSharedData.uploadedGifs.push(formattedGif);
  state.drafts.forEach((draft) => addDraftGif(draft.id, formattedGif));
};

const removeDraftGif = monitorComposerLastInteractedWith((id) => {
  ComposerStore.getDraft(id).gif = null;
  ComposerActionCreators.updateDraftCharacterCount(id, {
    didEditorStateChange: false,
  });
});

const getThreadedDraftData = (draft) => {
  return {
    text: EditorStateProxy.getPlainText(draft.editorState),
    images: [...draft.images],
    video: draft.video,
    gif: draft.gif,
    link: draft.link,
    retweet: draft.retweet,
    enabledAttachmentType: draft.enabledAttachmentType,
  };
};

const getNewThreadedDraftData = () => {
  return {
    text: '',
    images: [],
    video: null,
    gif: null,
    link: null,
    retweet: null,
    enabledAttachmentType: AttachmentTypes.MEDIA,
  };
};

const updateDraftAddTweet = monitorComposerLastInteractedWith(
  (id, didEditorStateChange) => {
    const draft = ComposerStore.getDraft(id);
    if (draft.updateType !== 'thread') {
      draft.updateType = 'thread';
    }

    if (!Array.isArray(draft.thread)) {
      draft.thread = [];
    }

    // preserve last active draft
    draft.thread[state.meta.activeThreadId] = getThreadedDraftData(draft);

    // create a new draft entity
    const indexToInsert = state.meta.activeThreadId + 1;
    draft.thread.splice(indexToInsert, 0, getNewThreadedDraftData());

    // remove currently uploaded media
    draft.images = [];
    draft.video = null;
    draft.gif = null;
    draft.link = null;
    draft.retweet = null;
    draft.enabledAttachmentType = AttachmentTypes.MEDIA;

    // replace editor with empty text
    setDraftEditorState(
      id,
      EditorStateProxy.createStateFromText(
        draft.editorState,
        '',
        draft.service.name,
        {
          annotations: [],
          facebookMentionEntities: [],
        }
      )
    );

    // set active thread id
    setActiveThreadId(indexToInsert);
    AppActionCreators.forceEditorFocus();

    if (!didEditorStateChange) draft.forceDecoratorsRerender = true;
  }
);

const updateDraftThread = monitorComposerLastInteractedWith(
  (id, thread, didEditorStateChange) => {
    const draft = ComposerStore.getDraft(id);

    draft.thread = thread;

    if (!didEditorStateChange) draft.forceDecoratorsRerender = true;
  }
);

const deleteThreadedDraft = (id, threadId = null) => {
  const draft = ComposerStore.getDraft(id);
  const { activeThreadId } = state.meta;
  const threadToDelete = threadId || activeThreadId;
  draft.thread.splice(threadToDelete, 1);
  replaceEditorContents(
    id,
    draft,
    threadToDelete === draft.thread.length
      ? draft.thread.length - 1
      : threadToDelete
  );
  if (draft.thread.length === 1) {
    draft.thread = null;
    draft.updateType = null;
  }
};

const setActiveThreadId = (threadId) => {
  state.meta.activeThreadId = threadId;
};

const replaceEditorContents = (id, draft, threadId) => {
  // reset media available in editor
  draft.images = [];
  draft.link = null;
  draft.gif = null;
  draft.video = null;
  draft.retweet = null;

  // fetch draft we switch to
  const newDraft = draft.thread[threadId];

  // replace editor text
  setDraftEditorState(
    id,
    EditorStateProxy.createStateFromText(
      draft.editorState,
      newDraft.text,
      draft.service.name,
      {
        annotations: [],
        facebookMentionEntities: [],
      }
    )
  );

  // add images if available
  if (Array.isArray(newDraft.images) && newDraft.images.length > 0) {
    newDraft.images.forEach((image) => {
      const formattedImage = getNewImage({
        url: image.url,
        source: image.source,
        altText: image.altText,
      });
      addDraftImage(id, formattedImage);
    });
  }
  // add video if available
  if (newDraft.video) {
    addDraftVideo(id, newDraft.video);
  }
  // add gif if available
  if (newDraft.gif) {
    addDraftGif(id, newDraft.gif);
  }
  // add link if available
  if (newDraft.link) {
    draft.link = newDraft.link;
  }
  if (newDraft.retweet) {
    draft.retweet = newDraft.retweet;
  }
  draft.enabledAttachmentType = newDraft.enabledAttachmentType;
  setActiveThreadId(threadId);
  ComposerActionCreators.updateDraftCharacterCount(id, {
    didEditorStateChange: false,
  });
  AppActionCreators.forceEditorFocus();
};

const switchActiveThreadEditor = (id, threadId) => {
  const draft = ComposerStore.getDraft(id);
  const currentlyActiveThreadId = state.meta.activeThreadId;
  // preserve current composer data in thread structure
  draft.thread[currentlyActiveThreadId] = getThreadedDraftData(draft);
  replaceEditorContents(id, draft, threadId);
};

// This method can be called very often (onMouseMove), so make sure the store
// only emits a change if a change actually happened to prevent over-rendering
const updateDraftTempImage = (id, url) => {
  const draft = ComposerStore.getDraft(id);

  if (draft.tempImage !== url) {
    draft.tempImage = url;
    return true;
  }

  return false;
};

// Remove draft temp image in all cases if no image is specified, otherwise only
// remove the passed draft temp image if it's specified
const removeDraftTempImage = (id, url = null) => {
  if (url === null || ComposerStore.getDraft(id).tempImage === url) {
    updateDraftTempImage(id, null);
  }
};

const addDraftRetweet = (id, retweetData) => {
  const draft = ComposerStore.getDraft(id);

  if (!draft.service.canHaveAttachmentType(AttachmentTypes.RETWEET)) return;

  const retweet = getNewRetweet(retweetData);
  draft.retweet = retweet;

  ComposerActionCreators.updateDraftCharacterCount(id, {
    didEditorStateChange: false,
  });
};

const disableDraftsAttachment = monitorComposerLastInteractedWith(
  (id, attachmentType, didEditorStateChange) => {
    const draft = ComposerStore.getDraft(id);
    draft.enabledAttachmentType = null;

    if (attachmentType === AttachmentTypes.LINK) {
      draft.link = null;
    }

    ComposerActionCreators.attachmentToggled(id);
    ComposerActionCreators.updateDraftCharacterCount(id, {
      didEditorStateChange: false,
    });

    if (!didEditorStateChange) draft.forceDecoratorsRerender = true;
  }
);

const disableDraftAttachment = (id) => {
  const draft = ComposerStore.getDraft(id);
  if (draft.service.canHaveMediaAttachmentType(AttachmentTypes.MEDIA)) {
    draft.enabledAttachmentType = AttachmentTypes.MEDIA;
  } else {
    draft.enabledAttachmentType = null;
  }

  ComposerActionCreators.attachmentToggled(id);
  ComposerActionCreators.updateDraftCharacterCount(id, {
    didEditorStateChange: false,
  });
};

const enableDraftAttachment = (id, attachmentType) => {
  const draft = ComposerStore.getDraft(id);

  if (!draft.service.canHaveAttachmentType(attachmentType)) return;

  // Link Attachment doesn't have a suggestion mode: don't enable it if no link attached
  if (
    attachmentType === AttachmentTypes.LINK &&
    !ComposerStore.doesDraftHaveLinkAttachment(id)
  ) {
    return;
  }

  // Retweet Attachment doesn't have a suggestion mode: don't enable it if no retweet attached
  if (
    attachmentType === AttachmentTypes.RETWEET &&
    !ComposerStore.doesDraftHaveRetweetAttachment(id)
  ) {
    return;
  }

  // TikTok doesn't have a suggestion mode: don't enable it if service is TikTok
  if (id === 'tiktok') return;

  draft.enabledAttachmentType = attachmentType;

  ComposerActionCreators.attachmentToggled(id);
  ComposerActionCreators.updateDraftCharacterCount(id, {
    didEditorStateChange: false,
  });
  if (id === 'instagram') ComposerActionCreators.updateInstagramState();
};

const toggleDraftAttachment = monitorComposerLastInteractedWith(
  (id, attachmentType) => {
    const draft = ComposerStore.getDraft(id);
    const shouldDisableAttachment =
      draft.enabledAttachmentType === attachmentType;

    if (shouldDisableAttachment) {
      disableDraftAttachment(id);
    } else {
      enableDraftAttachment(id, attachmentType);
    }

    if (id === 'instagram') ComposerActionCreators.updateInstagramState();
  }
);

const replaceDraftLinkWithShortlink = (id, link, shortLink) => {
  const draft = ComposerStore.getDraft(id);
  const { editorState } = draft;

  draft.editorState = EditorStateProxy.replaceLinkWithShortLink(
    editorState,
    link,
    shortLink
  );

  ComposerActionCreators.parseDraftTextLinks(id);
};

const addDraftUnshortenedLink = (id, unshortenedLink) => {
  const draft = ComposerStore.getDraft(id);
  draft.unshortenedUrls.push(unshortenedLink);
};

const addDraftAvailableImages = (id, images, sourceLink) => {
  const draft = ComposerStore.getDraft(id);

  const newAvailableImages = images.map((image) => {
    const formattedImage = getNewImage({
      url: image.url,
      width: image.width,
      height: image.height,
    });

    return getNewAvailableImage(formattedImage, sourceLink);
  });

  draft.availableImages = draft.availableImages.concat(newAvailableImages);
};

const removeDraftAvailableImages = (id, sourceLink) => {
  const draft = ComposerStore.getDraft(id);

  draft.availableImages = draft.availableImages.filter(
    (availableImage) => availableImage.sourceLink !== sourceLink
  );
};

const addOmniNotice = (message, draftId) => {
  NotificationActionCreators.queueInfo({
    scope: NotificationScopes.MC_OMNIBOX_EDIT_NOTICE,
    message,
    onlyCloseOnce: false,
    data: { id: draftId, type: 'notice' },
  });
};

const getEditedImagesMessage = (service) => {
  let message;
  if (service.maxAttachableImagesCount === 1) {
    message = `${service.formattedName} does not support multiple images, so we’ve used your first image`;
  } else {
    message = `${service.formattedName} allows ${service.maxAttachableImagesCount}
    images, so we kept the first ${service.maxAttachableImagesCount} images`;
  }
  return message;
};

/**
 * Copies draft media
 * @param draftFromMedia -> simplified Draft entity containing only media:
 * {
 *   enabledAttachmentType: string
 *   retweet: Retweet,
 *   video: Video,
 *   gif: Gif,
 *   images: Image[],
 * }
 * @param draftTo -> full entity from Draft.ts
 */
const copyDraftMedia = (draftFromMedia, draftTo) => {
  if (draftFromMedia.images.length > 0) {
    if (!draftTo.service.canHaveMediaAttachmentType(MediaTypes.IMAGE)) {
      const message = `${draftTo.service.formattedName} doesn't allow images, so we've removed it for you!`;
      addOmniNotice(message, draftTo.id);
      return;
    }

    let imagesToAttach = draftFromMedia.images;
    if (
      draftFromMedia.images.length > draftTo.service.maxAttachableImagesCount
    ) {
      imagesToAttach = draftFromMedia.images.slice(
        0,
        draftTo.service.maxAttachableImagesCount
      );
      const message = getEditedImagesMessage(draftTo.service);
      addOmniNotice(message, draftTo.id);
    }
    imagesToAttach.forEach((image) => addDraftImage(draftTo.id, image));
    enableDraftAttachment(draftTo.id, AttachmentTypes.MEDIA);
  } else if (draftFromMedia.gif !== null) {
    if (!draftTo.service.canHaveMediaAttachmentType(MediaTypes.GIF)) {
      const message = `${draftTo.service.formattedName} doesn't allow GIFs, so we've removed it for you!`;
      addOmniNotice(message, draftTo.id);
      return;
    }
    addDraftGif(draftTo.id, draftFromMedia.gif);
    enableDraftAttachment(draftTo.id, AttachmentTypes.MEDIA);
  } else if (draftFromMedia.video !== null) {
    if (!draftTo.service.canHaveMediaAttachmentType(MediaTypes.VIDEO)) {
      const message = `${draftTo.service.formattedName} doesn't allow videos, so we've removed it for you!`;
      addOmniNotice(message, draftTo.id);
      return;
    }

    // Re-validate TikTok videos before attaching
    if (draftTo.service.name === 'tiktok') {
      if (draftFromMedia.video.url) {
        const extension = draftFromMedia.video.url
          .split(/[#?]/)[0]
          .split('.')
          .pop()
          .trim()
          .toUpperCase();

        if (!draftTo.service.canHaveMediaAttachmentType(extension)) {
          const message = `${draftTo.service.formattedName} doesn't allow videos of this file type, so we've removed it for you!`;
          addOmniNotice(message, draftTo.id);
          return;
        }
      }

      const validationResultVideo = validateVideo(
        draftFromMedia.video,
        draftTo.service
      );

      if (validationResultVideo.isInvalid()) {
        let { message } = validationResultVideo;
        message += ", so we've removed the video for you!";
        addOmniNotice(message, draftTo.id);
        return;
      }
    }

    addDraftVideo(draftTo.id, draftFromMedia.video);
    enableDraftAttachment(draftTo.id, AttachmentTypes.MEDIA);
  }
};

const replaceDraftLinkWithUnshortenedLink = (
  id,
  unshortenedLink,
  shortLink
) => {
  const draft = ComposerStore.getDraft(id);
  const { editorState } = draft;

  draft.editorState = EditorStateProxy.replaceLinkWithUnshortenedLink(
    editorState,
    unshortenedLink,
    shortLink
  );

  ComposerActionCreators.parseDraftTextLinks(id);
};

const copyDraftTextData = (draftFrom, draftTo) => {
  let newEditorState = EditorStateProxy.copyDraftEditorState(
    draftFrom,
    draftTo
  );

  if (
    draftFrom.retweet &&
    draftFrom.retweet.text &&
    draftFrom.retweet.text.trim() !== ''
  ) {
    newEditorState = EditorStateProxy.createStateFromText(
      newEditorState,
      `${EditorStateProxy.getPlainText(draftFrom.editorState)}\n${
        draftFrom.retweet.text
      }`,
      draftTo.id
    );
  }

  // If we copy from Thread, we want all threaded updates to be copied
  if (draftFrom.hasThread()) {
    const joinedThreadsText = DraftMethods.getJoinedThreadsText(draftFrom);
    newEditorState = EditorStateProxy.createStateFromText(
      newEditorState,
      joinedThreadsText,
      draftTo.id
    );
  }

  setDraftEditorState(draftTo.id, newEditorState);

  const unshortenedUrls = [];
  if (
    (draftTo.service.isPinterest() || draftTo.service.isMastodon()) &&
    draftFrom?.urls.length > 0
  ) {
    draftFrom.urls.forEach((url) => {
      replaceDraftLinkWithUnshortenedLink(
        draftTo.id,
        ComposerStore.getCanonicalUrl(url),
        url
      );
      unshortenedUrls.push(ComposerStore.getCanonicalUrl(url));
    });
    draftFrom.urls = unshortenedUrls;
    draftFrom.unshortenedUrls = unshortenedUrls;
  }

  draftTo.urls = draftFrom.urls;
  draftTo.unshortenedUrls = draftFrom.unshortenedUrls;
  draftTo.shortLinkLongLinkMap = draftFrom.shortLinkLongLinkMap;
};

const copyDraftContents = ({
  draftFrom = ComposerStore.getDraft('omni'),
  draftsTo = state.drafts,
} = {}) => {
  draftsTo.forEach((draft) => {
    const { service } = draft;
    if (service.isOmni || draft === draftFrom) return;

    // eslint-disable-next-line no-use-before-define
    clearDraft(draft.id, { preserveEnabledState: true });

    // Add text and data related to text
    copyDraftTextData(draftFrom, draft);

    draft.sourceLink = draftFrom.sourceLink;
    draft.availableImages = draftFrom.availableImages;

    /**
     * Below we add attachments. draftFromMedia is a structure created from the draftFrom,
     * or its active threaded draft (in case of Thread updates)
     * @type {{images: Image[], retweet: Retweet, gif: Gif, link: Link, enabledAttachmentType: string, video: Video}}
     */
    const draftFromMedia = {
      enabledAttachmentType: draftFrom.hasThread()
        ? draftFrom.thread[0].enabledAttachmentType
        : draftFrom.enabledAttachmentType,
      link: draftFrom.hasThread() ? draftFrom.thread[0].link : draftFrom.link,
      retweet: draftFrom.hasThread()
        ? draftFrom.thread[0].retweet
        : draftFrom.retweet,
      video: draftFrom.hasThread()
        ? draftFrom.thread[0].video
        : draftFrom.video,
      gif: draftFrom.hasThread() ? draftFrom.thread[0].gif : draftFrom.gif,
      images: draftFrom.hasThread()
        ? draftFrom.thread.reduce(
            (allImages, threadedUpdate) =>
              allImages.concat(threadedUpdate.images),
            []
          )
        : draftFrom.images,
    };

    if (draftFromMedia.enabledAttachmentType) {
      switch (draftFromMedia.enabledAttachmentType) {
        case AttachmentTypes.LINK: {
          if (
            !service.canHaveAttachmentType(draftFromMedia.enabledAttachmentType)
          ) {
            const message = `${draft.service.formattedName} doesn't allow link attachments, so we removed it for you!`;
            addOmniNotice(message, draft.id);
            return;
          }

          if (!service.canEditLinkAttachment && draftFromMedia.link.wasEdited) {
            const message = `${draft.service.formattedName} doesn't allow editing links, so we kept the original link attachment for you!`;
            addOmniNotice(message, draft.id);
          }

          if (draftFromMedia.link !== null) {
            const linkDataToReuse = service.canEditLinkAttachment
              ? {
                  url: draftFromMedia.link.url,
                  title: draftFromMedia.link.title,
                  description: draftFromMedia.link.description,
                  thumbnail:
                    draftFromMedia.link.thumbnail !== null
                      ? draftFromMedia.link.thumbnail.url
                      : null,
                }
              : {
                  url: draftFromMedia.link.url,
                };

            updateDraftLinkData(draft.id, linkDataToReuse, {
              isNewLinkAttachment: true,
              comesFromDirectUserAction: false,
            });
            enableDraftAttachment(draft.id, AttachmentTypes.LINK);
          }

          break;
        }

        case AttachmentTypes.MEDIA: {
          if (
            !service.canHaveAttachmentType(draftFromMedia.enabledAttachmentType)
          ) {
            const message = `${draft.service.formattedName} doesn't allow image attachments, so we removed the images for you!`;
            addOmniNotice(message, draft.id);
            return;
          }

          if (
            draftFromMedia.video !== null &&
            service.videoMaxSize &&
            draftFromMedia.video.size > service.videoMaxSize
          ) {
            const message = `${
              draft.service.formattedName
            } allows video size to be up to ${Math.round(
              service.videoMaxSize / 1024 / 1024
            )}MB.`;
            addOmniNotice(message, draft.id);
          }

          copyDraftMedia(draftFromMedia, draft);
          break;
        }

        case AttachmentTypes.RETWEET: {
          if (
            !service.canHaveAttachmentType(draftFromMedia.enabledAttachmentType)
          ) {
            const message = `${draft.service.formattedName} doesn't allow retweet attachments, so we removed it for you!`;
            addOmniNotice(message, draft.id);
            return;
          }

          draft.retweet = draftFromMedia.retweet;
          break;
        }

        default:
          break;
      }
    }
  });

  // Let draftFrom remain the last active draft, so that networks that are added
  // immediately after still use the same draft as reference (instead of a
  // possibly more restricted other network, e.g. Twitter)
  state.meta.lastInteractedWithComposerId = draftFrom.id;
  ComposerActionCreators.updateInstagramState();
};

const clearDraft = (id, { preserveEnabledState = false } = {}) => {
  const draft = ComposerStore.getDraft(id);
  const emptyDraft = getNewDraft(draft.service);

  if (preserveEnabledState) emptyDraft.isEnabled = draft.isEnabled;

  Object.assign(draft, emptyDraft, { scheduledAt: draft.scheduledAt });
};

const clearOmniboxDraftWhenEnabling = (willBeEnabled) => {
  if (willBeEnabled) clearDraft('omni');
};

const parseDraftsTextLinks = () => {
  const enabledDrafts = ComposerStore.getEnabledDrafts();
  enabledDrafts.forEach((draft) => parseDraftTextLinks(draft.id));
};

const insertHashtag = ({ draft, text }) => {
  const { id, editorState } = draft;
  const newEditorState = EditorStateProxy.insertHashtag(editorState, text);
  setDraftEditorState(id, newEditorState);
};

const insertHashtagInCaption = (text) => {
  const { lastInteractedWithComposerId } = ComposerStore.getMeta();
  const draft = ComposerStore.getDraft(lastInteractedWithComposerId);

  if (draft) {
    insertHashtag({ draft, text });
  } else {
    const enabledDrafts = ComposerStore.getEnabledDrafts();
    enabledDrafts.forEach((enabledDraft) => {
      insertHashtag({ draft: enabledDraft, text });
    });
  }
};

const passesImageAspectRatioTest = (image) => {
  if (!image.width || !image.height) return true;
  const ratio = image.width / image.height;
  return (
    ratio >= InstagramAspectRatioLimits.min &&
    ratio <= InstagramAspectRatioLimits.max
  );
};

const passesImagesAspectRatioTest = (images) => {
  return images.every(passesImageAspectRatioTest);
};

const isVideoAspectRatioOk = (draft) => {
  const { video, service } = draft;
  const aspectRatio = video.width / video.height;

  if (draft.isReelsPost()) {
    return (
      aspectRatio >= service.videoMinAspectRatioReels &&
      aspectRatio <= service.videoMaxAspectRatioReels
    );
  }

  if (draft.isStoryPost()) {
    return (
      aspectRatio >= service.videoMinAspectRatioStories &&
      aspectRatio <= service.videoMaxAspectRatioStories
    );
  }

  const minAspectRatio = service.videoMinAspectRatio;
  const maxAspectRatio = service.videoMaxAspectRatio;
  return aspectRatio >= minAspectRatio && aspectRatio <= maxAspectRatio;
};

const getUsernamesOfProfilesWithoutPushNotifications = ({
  selectedIgProfiles,
}) => {
  const usernamesOfProfilesWithoutPushNotifications = selectedIgProfiles
    .filter((profile) => !profile.hasPushNotifications)
    .map((profile) => profile.service && `@${profile.service.username}`);

  return usernamesOfProfilesWithoutPushNotifications.join(', ');
};

const getReminderMessage = (selectedIgProfiles, mediaType) => {
  const draftsMode = AppStore.getMetaData().tabId === 'drafts';

  const hasSomeProfilesWithoutPushNotifications = selectedIgProfiles.some(
    (profile) => !profile.hasPushNotifications
  );
  const hasSomeProfilesWithPushNotifications = selectedIgProfiles.some(
    (profile) => profile.hasPushNotifications
  );

  const usernameList =
    hasSomeProfilesWithoutPushNotifications &&
    getUsernamesOfProfilesWithoutPushNotifications({ selectedIgProfiles });

  // Only IG profiles with Push Notifications enabled
  const hasAllProfilesWithPushNotifications =
    hasSomeProfilesWithPushNotifications &&
    !hasSomeProfilesWithoutPushNotifications;

  // Only IG profiles with Push Notifications disabled
  const hasAllProfilesWithoutPushNotifications =
    hasSomeProfilesWithoutPushNotifications &&
    !hasSomeProfilesWithPushNotifications;

  // Has both IG Profiles with and without Push Notifications enabled
  const hasProfilesWithAndWithoutPushNotifications =
    hasSomeProfilesWithPushNotifications &&
    hasSomeProfilesWithoutPushNotifications;

  const messageMap = {
    imageRatio: {
      onlyWith: {
        message: `Due to Instagram limitations, we can't post images directly to Instagram with aspect
          ratios outside the range 4:5 to 1.91:1. You will receive a Reminder to post manually when the time comes!`,
        composerId: 'instagram',
        code: 'ASPECT_RATIO',
      },
      onlyWithout: {
        message: `To post images to Instagram with aspect ratio outside the 4:5 to 1:91:1 range, you’ll need
          to set up Reminders. Reminders aren’t set up for ${usernameList}. Finish scheduling your post, then
          visit the queue for ${usernameList} to set up Reminders!`,
        composerId: 'instagram',
        code: 'ASPECT_RATIO',
      },
      mixed: {
        message: `Reminders aren’t set up for ${usernameList}. To post an image outside the range 4:5 to 1.91:1
          to Instagram, you’ll need to set up Reminders. Finish scheduling your post, then visit the queue for
          ${usernameList} to set up Reminders!`,
        composerId: 'instagram',
        code: 'ASPECT_RATIO',
      },
    },
    videoRatio: {
      onlyWith: {
        message: `Due to Instagram limitations, we can't post videos directly to Instagram with aspect
        ratios outside the range 4:5 to 16:9. You will receive a Reminder to post manually when the time comes!`,
        composerId: 'instagram',
        code: 'ASPECT_RATIO',
      },
      onlyWithout: {
        message: `To post videos to Instagram with aspect ratio outside the 4:5 to 16:9 range, you’ll need
          to set up Reminders. Reminders aren’t set up for ${usernameList}. Finish scheduling your post,
          then visit the queue for ${usernameList} to set up Reminders!`,
        composerId: 'instagram',
        code: 'ASPECT_RATIO',
      },
      mixed: {
        message: `Reminders aren’t set up for ${usernameList}. To post a video outside the range 4:5 to 16:9 to Instagram,
          you’ll need to set up Reminders. Finish scheduling your post, then visit the queue for ${usernameList} to set up Reminders!`,
        composerId: 'instagram',
        code: 'ASPECT_RATIO',
      },
    },
    mixScheduling: {
      onlyWith: {
        message: `Some of your channels aren't enabled for Direct Scheduling, we'll send out Reminders
          for those accounts. Not all features are supported for reminders.`,
        composerId: 'instagram',
        code: 'NOT_ENABLED',
      },
      onlyWithout: {
        message: `Some of your channels aren't enabled for Direct Scheduling, you'll need to set up
          Reminders for those accounts. Reminders aren’t set up for ${usernameList}. Finish scheduling your post,
          then visit the queue for ${usernameList} to set up Reminders!`,
        composerId: 'instagram',
        code: 'NOT_ENABLED',
      },
      mixed: {
        message: `Some of your channels aren't enabled for Direct Scheduling, we'll send out Reminders for those accounts.
          Reminders aren’t set up for ${usernameList}. Finish scheduling your post, then visit the queue for ${usernameList} to set up Reminders!`,
        composerId: 'instagram',
        code: 'NOT_ENABLED',
      },
    },
    remindersOnly: {
      onlyWith: {
        message: `Your channel isn't enabled for Direct Scheduling, we'll send out Reminders. Not all features are supported for reminders.`,
        composerId: 'instagram',
        code: 'NOT_ENABLED',
      },
      onlyWithout: {
        message: `Reminders aren’t set up for ${usernameList}. Finish scheduling your post,
          then visit the queue for ${usernameList} to set up Reminders!`,
        composerId: 'instagram',
        code: 'NOT_ENABLED',
      },
      mixed: {
        message: `Some of your channels aren't enabled for Direct Scheduling, we'll send out Reminders for those channels.
          Reminders aren’t set up for ${usernameList}. Finish scheduling your post, then visit the queue for ${usernameList} to set up Reminders!`,
        composerId: 'instagram',
        code: 'NOT_ENABLED',
      },
    },
  };
  const messagesByMediaType = messageMap[mediaType] || {};
  let feedbackMessage;

  if (hasAllProfilesWithPushNotifications) {
    feedbackMessage = messagesByMediaType.onlyWith;
  }

  if (hasAllProfilesWithoutPushNotifications && !draftsMode) {
    feedbackMessage = messagesByMediaType.onlyWithout;
  } else if (hasAllProfilesWithoutPushNotifications) {
    feedbackMessage = messagesByMediaType.onlyWith;
  }

  if (hasProfilesWithAndWithoutPushNotifications && !draftsMode) {
    feedbackMessage = messagesByMediaType.mixed;
  } else if (hasProfilesWithAndWithoutPushNotifications) {
    feedbackMessage = messagesByMediaType.onlyWith;
  }

  if (feedbackMessage) {
    return getNewInstagramFeedbackObj(feedbackMessage);
  }
};

const updateInstagramDraftsFeedback = () => {
  const instagramDraft = ComposerStore.getEnabledDrafts().filter(
    (draft) => draft.id === 'instagram'
  )[0];
  if (!instagramDraft) return;
  instagramDraft.instagramFeedback = [];

  const selectedIgProfiles =
    AppStore.getSelectedProfilesForService('instagram');
  const hasSomeEnabledProfiles = selectedIgProfiles.some(
    (profile) => profile.instagramDirectEnabled
  );
  const hasSomeDisabledProfiles = selectedIgProfiles.some(
    (profile) => !profile.instagramDirectEnabled
  );

  const isMediaError =
    hasSomeEnabledProfiles &&
    instagramDraft.enabledAttachmentType === AttachmentTypes.MEDIA;
  const isImageOrGalleryError =
    isMediaError && instagramDraft.images?.length > 0;
  const isImageRatioError =
    isImageOrGalleryError &&
    passesImagesAspectRatioTest(instagramDraft.images) === false;
  const isVideoRatioError =
    isMediaError &&
    instagramDraft.video &&
    isVideoAspectRatioOk(instagramDraft) === false;
  const hasMixSchedulingError =
    hasSomeEnabledProfiles && hasSomeDisabledProfiles;

  const hasInstagramProfileWithReminders = hasSomeDisabledProfiles;

  let feedbackObject;

  if (isImageRatioError) {
    if (instagramDraft.isStoryPost()) {
      // We don't support reminders for Stories at the moment
      return;
    }
    feedbackObject = getReminderMessage(selectedIgProfiles, 'imageRatio');
  } else if (isVideoRatioError) {
    if (instagramDraft.isReelsPost() || instagramDraft.isStoryPost()) {
      // We don't support reminders for Reels and Stories at the moment
      return;
    }
    feedbackObject = getReminderMessage(selectedIgProfiles, 'videoRatio');
  } else if (hasMixSchedulingError) {
    feedbackObject = getReminderMessage(selectedIgProfiles, 'mixScheduling');
  } else if (hasInstagramProfileWithReminders) {
    feedbackObject = getReminderMessage(selectedIgProfiles, 'remindersOnly');
  }

  if (feedbackObject) {
    instagramDraft.instagramFeedback.push(feedbackObject);
  }
};

const onDispatchedPayload = (payload) => {
  const { action } = payload;
  let shouldEmitChange = true;
  let video;

  switch (action.actionType) {
    case ActionTypes.COMPOSER_SET_DRAFTS_INITIAL_TEXT:
      setDraftsInitialText(action);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_EDITOR_STATE:
      setDraftEditorState(action.id, action.editorState);
      break;

    case ActionTypes.UPDATE_DRAFT_IS_SAVED:
      updateDraftIsSaved(action.id);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_CHARACTER_COUNT:
      updateDraftCharacterCount(action.id, action.didEditorStateChange);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_COMMENT:
      updateDraftComment(action.id, action.commentText);
      break;

    case ActionTypes.COMPOSER_HIDE_SUGGESTED_MEDIA:
      hideSuggestedMedia();
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_SHOPGRID_LINK:
      updateShopgridLink(action.id, action.shopgridLink);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_SHOPGRID_LINK:
      state.drafts.forEach((draft) =>
        updateShopgridLink(draft.id, action.shopgridLink)
      );
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_UPDATE_TYPE:
      state.drafts.forEach((draft) =>
        updateDraftUpdateType(draft.id, action.updateType)
      );
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_UPDATE_TYPE:
      updateDraftUpdateType(action.id, action.updateType);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_GOOGLE_BUSINESS_DATA:
      updateDraftChannelData(SERVICE_GOOGLEBUSINESS, action.googleBusinessData);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_START_PAGE_DATA:
      updateDraftChannelData(SERVICE_STARTPAGE, action.startPageData);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_INSTAGRAM_DATA:
      updateDraftChannelData(SERVICE_INSTAGRAM, action.instagramData);
      updateInstagramDraftsFeedback();
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_YOUTUBE_DATA:
      updateDraftChannelData(SERVICE_YOUTUBE, action.youtubeData);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_COMMENT:
      state.drafts.forEach((draft) =>
        updateDraftComment(draft.id, action.commentText)
      );
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_IMAGE_USER_TAGS:
      // need to add timeout because draft images doesn't load right away in edit mode
      setTimeout(() => {
        state.drafts.forEach((draft) =>
          updateDraftImageUserTags(draft.id, action && action.userTags)
        );
      });
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_CAMPAIGN_ID:
      state.drafts.forEach((draft) =>
        updateDraftCampaignId(draft.id, action?.campaignId)
      );
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_TITLE:
      state.drafts.forEach((draft) =>
        updateDraftTitle(draft.id, action?.title)
      );
      break;

    case ActionTypes.COMPOSER_UPDATE_TOGGLE_SIDEBAR:
      updateToggleSidebarVisibility(action.id, action.composerSidebarVisible);
      break;

    case ActionTypes.COMPOSER_UPDATE_HASHTAG_PAYWALL:
      updateToggleSidebarVisibility(action.id, action.showHashtagPaywall);
      break;

    case ActionTypes.UPDATE_CONNECT_CHANNEL_POPOVER_VISIBLE:
      updateToggleSidebarVisibility(
        action.id,
        action.connectChannelPopoverVisible
      );
      break;

    case ActionTypes.COMPOSER_UPDATE_TOGGLE_POST_PREVIEW:
      updateTogglePostPreviewVisibility(
        action.id,
        action.composerPostPreviewVisible
      );
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_COMMENT_CHARACTER_COUNT:
      updateDraftCommentCharacterCount(action.id, action.didEditorStateChange);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_TITLE_CHARACTER_COUNT:
      updateDraftTitleCharacterCount(action.id, action.didEditorStateChange);
      break;

    case ActionTypes.COMPOSER_PARSE_DRAFT_TEXT_LINKS:
      parseDraftTextLinks(action.id);
      break;

    case ActionTypes.COMPOSER_PARSE_MASTODON_TEXT:
      parseMastodonText();
      break;

    case ActionTypes.COMPOSER_PARSE_DRAFTS_TEXT_LINKS:
      parseDraftsTextLinks();
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_SCHEDULED_AT:
      state.drafts.forEach((draft) => {
        updateDraftScheduledAt(
          draft.id,
          action.scheduledAt,
          action.isPinnedToSlot
        );
      });
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_LINK_DATA:
      updateDraftLinkData(action.id, action.linkData, action.meta);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_LINK_DATA:
      state.drafts.forEach((draft) =>
        updateDraftLinkData(draft.id, action.linkData, action.meta)
      );
      break;

    case ActionTypes.COMPOSER_ADD_DRAFT_IMAGE:
      addDraftImage(action.id, action.image);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_LINK_THUMBNAIL:
      updateDraftLinkThumbnail(action.id, action.thumbnail);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_VIDEO_THUMBNAIL:
      updateDraftVideoThumbnail(action.id, action.thumbnail);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_VIDEO_TITLE:
      updateDraftVideoTitle(action.id, action.title);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_SOURCE_LINK:
      updateDraftSourceLink(action.id, action.url);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_SOURCE_LINK:
      state.drafts.forEach((draft) =>
        updateDraftSourceLink(draft.id, action.url)
      );
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_SOURCE_LINK_DATA:
      updateDraftSourceLinkData(action.id, action.linkData);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_LOCATION:
      updateDraftLocation(action.id, action.locationId, action.locationName);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_LOCATION:
      state.drafts.forEach((draft) =>
        updateDraftLocation(draft.id, action.locationId, action.locationName)
      );
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_LIST_PLACES:
      updateDraftListPlaces(action.id, action.places);
      break;

    case ActionTypes.COMPOSER_UPDATE_INSTAGRAM_DRAFT_THUMBNAIL:
      updateInstagramDraftThumbnail(
        action.id,
        action.thumbOffset,
        action.thumbnail
      );
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_THUMBNAIL_GENERATED: {
      updateDraftThumbnailGenerated(action.draftId, action.thumbnailURL);

      const { videoMetadata } = action;

      const processedVideoData = {
        uploadId: action.uploadId,
        name: action.name,
        duration: videoMetadata.duration,
        durationMs: videoMetadata.durationMs,
        frameRate: videoMetadata.frameRate,
        size: videoMetadata.size,
        width: videoMetadata.width,
        height: videoMetadata.height,
        url: videoMetadata.url,
        originalUrl: videoMetadata.url,
        thumbnail: action.thumbnailURL,
        availableThumbnails: [action.thumbnailURL],
        thumbOffset: 0,
        mediaType: MediaTypes.VIDEO,
      };

      addDraftVideo(action.draftId, processedVideoData);
      addSharedUploadedVideo(processedVideoData);
      if (action.uploaderInstance) {
        completeUploadProgress(action.draftId, action.uploaderInstance);
      }
      break;
    }

    case ActionTypes.COMPOSER_DRAFT_IMAGE_ADDED:
      removeDraftTempImage(action.id, action.url);
      break;

    case ActionTypes.COMPOSER_REMOVE_DRAFT_IMAGE:
      removeDraftImage(action.id, action.image);
      break;

    case ActionTypes.COMPOSER_ADD_DRAFT_VIDEO:
      addDraftVideo(action.id, action.video);
      break;

    case ActionTypes.COMPOSER_ADD_DRAFT_GIF:
      addDraftGif(action.id, action.gif);
      break;

    case ActionTypes.COMPOSER_DRAFT_VIDEO_ADDED:
      removeDraftTempImage(action.id, action.video.thumbnail);
      break;

    case ActionTypes.COMPOSER_DRAFT_GIF_ADDED:
      removeDraftTempImage(action.id, action.url);
      break;

    case ActionTypes.COMPOSER_REMOVE_DRAFT_VIDEO:
      removeDraftVideo(action.id, action.videoUrl);
      break;

    case ActionTypes.COMPOSER_REMOVE_DRAFT_GIF:
      removeDraftGif(action.id, action.gifUrl);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_IMAGE_USER_TAGS:
      updateDraftImageUserTags(action.id, action && action.userTags);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_CAMPAIGN_ID:
      updateDraftCampaignId(action.id, action?.campaignId);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_TITLE:
      updateDraftTitle(action.id, action?.title);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_TEMP_IMAGE:
      shouldEmitChange = updateDraftTempImage(action.id, action.url);
      break;

    case ActionTypes.COMPOSER_REMOVE_DRAFT_TEMP_IMAGE:
      removeDraftTempImage(action.id);
      break;

    case ActionTypes.COMPOSER_ADD_DRAFTS_RETWEET:
      state.drafts.forEach((draft) =>
        addDraftRetweet(draft.id, action.retweetData)
      );
      break;

    case ActionTypes.COMPOSER_TOGGLE_DRAFT_ATTACHMENT:
      toggleDraftAttachment(action.id, action.attachmentType);
      break;

    case ActionTypes.COMPOSER_DISABLE_DRAFT_ATTACHMENT:
      disableDraftsAttachment(
        action.id,
        action.attachmentType,
        action.didEditorStateChange
      );
      break;

    case ActionTypes.COMPOSER_ENABLE_DRAFTS_ATTACHMENT:
      state.drafts.forEach((draft) =>
        enableDraftAttachment(draft.id, action.attachmentType)
      );
      break;

    case ActionTypes.COMPOSER_ENABLE:
      shouldEmitChange = enableDraft(action.id, action.markAppAsLoadedWhenDone);
      break;

    case ActionTypes.COMPOSER_DISABLE:
      shouldEmitChange = disableDraft(action.id);
      break;

    case ActionTypes.UPDATE_DRAFT_HAS_SAVING_ERROR:
      updateDraftHasSavingError(action.id, action.hasSavingError);
      break;

    case ActionTypes.COMPOSER_CLEAR_INLINE_ERRORS:
      clearDraftInlineErrors(action.id);
      break;

    case ActionTypes.COMPOSER_DRAFT_LINK_SHORTENED:
      monitorComposerLastInteractedWith(() => {
        mapShortLinkWithLongLink(action.id, action.shortLink, action.link);
        replaceDraftLinkWithShortlink(action.id, action.link, action.shortLink);
      })();
      break;

    case ActionTypes.COMPOSER_DRAFT_LINK_UNSHORTENED:
      monitorComposerLastInteractedWith(() => {
        addDraftUnshortenedLink(action.id, action.unshortenedLink);
        parseDraftTextLinks(action.id);
        unmapShortLinkWithLongLink(action.id, action.shortLink);
      })();
      break;

    case ActionTypes.COMPOSER_DRAFT_LINK_RESHORTENED:
      monitorComposerLastInteractedWith(() => {
        parseDraftTextLinks(action.id);
        mapShortLinkWithLongLink(action.id, action.shortLink, action.link);
      })();
      break;

    case ActionTypes.COMPOSER_ADD_DRAFT_AVAILABLE_IMAGES:
      addDraftAvailableImages(action.id, action.images, action.sourceLink);
      break;

    case ActionTypes.COMPOSER_REMOVE_DRAFT_AVAILABLE_IMAGES:
      removeDraftAvailableImages(action.id, action.sourceLink);
      break;

    case ActionTypes.COMPOSER_DRAFT_FILE_UPLOAD_STARTED:
      monitorComposerLastInteractedWith(() => {
        updateDraftFileUploadProgress(action.id, action.uploaderInstance, 0);
      })();
      break;

    case ActionTypes.COMPOSER_DRAFT_FILE_UPLOAD_PROGRESS:
      updateDraftFileUploadProgress(
        action.id,
        action.uploaderInstance,
        action.progress
      );
      break;

    case ActionTypes.COMPOSER_ADD_DRAFT_UPLOADED_IMAGE:
      if (action.uploaderInstance) {
        updateDraftFileUploadProgress(action.id, action.uploaderInstance, null);
      }

      // Order matters, this should remain last
      addDraftUploadedImage(
        action.id,
        action.url,
        action.width,
        action.height,
        action.source
      );
      break;

    case ActionTypes.UPLOADED_LINK_THUMBNAIL:
      addDraftUploadedLinkThumbnail(
        action.id,
        action.url,
        action.width,
        action.height
      );
      break;

    case ActionTypes.COMPOSER_ADD_DRAFT_UPLOADED_LINK_THUMBNAIL:
      addDraftUploadedLinkThumbnail(
        action.id,
        action.url,
        action.width,
        action.height
      );
      updateDraftFileUploadProgress(action.id, action.uploaderInstance, null);
      break;

    case ActionTypes.COMPOSER_ADD_DRAFT_UPLOADED_VIDEO:
      state.draftsSharedData.processingVideos.set(action.uploadId, [
        action.id,
        action.uploaderInstance,
      ]);
      break;

    case ActionTypes.COMPOSER_ADD_DRAFT_UPLOADED_VIDEO_WITHOUT_TRANSCODING: {
      generateThumbnailFromVideo(action.file)
        .then(({ imageFile }) => {
          const { id: userId, s3UploadSignature } = AppStore.getUserData();
          const uploader = getOrCreateUploader({
            id: 'thumbnail-uploader',
            s3UploadSignature: {
              ...s3UploadSignature,
              // this renaming happened inside composer userData importing,
              // it should be cleaned up as we refactor Composer importData utils
              base64policy: s3UploadSignature.base64Policy,
              success_action_status: s3UploadSignature.successActionStatus,
            },
            userId,
          });
          return uploader.uploadDirect(imageFile);
        })
        .then((uploadResult) => {
          AppDispatcher.handleViewAction({
            actionType: ActionTypes.COMPOSER_UPDATE_DRAFT_THUMBNAIL_GENERATED,
            draftId: action.draftId,
            thumbnailURL: uploadResult.url,
            videoMetadata: action.videoMetadata,
            uploadId: action.uploadId,
            uploaderInstance: action.uploaderInstance,
            name: action.name,
          });
          // allows you to add to queue once upload is complete
          AppActionCreators.setThumbnailLoading(false);
        })
        .catch((error) => {
          logError(error);
          NotificationActionCreators.queueError({
            scope: NotificationScopes.FILE_UPLOAD,
            message:
              'Uh oh! It looks like we had an issue generating the thumbnail for the video. You can still create the update without it.',
          });
          AppActionCreators.setThumbnailLoading(false);
        });
      break;
    }

    case ActionTypes.COMPOSER_ADD_DRAFT_UPLOADED_GIF:
      addDraftUploadedGif(
        action.id,
        action.url,
        action.stillGifUrl,
        action.width,
        action.height
      );
      if (action.uploaderInstance) {
        updateDraftFileUploadProgress(action.id, action.uploaderInstance, null);
      }
      break;

    case ActionTypes.COMPOSER_VIDEO_PROCESSED: {
      const videoData = action.processedVideoData;
      const { processingVideos } = state.draftsSharedData;

      if (!processingVideos.has(videoData.uploadId)) return;

      const [draftId, uploaderInstance] = processingVideos.get(
        videoData.uploadId
      );
      const newVideo = getNewVideo(videoData);

      addDraftVideo(draftId, newVideo);
      addSharedUploadedVideo(newVideo);
      if (uploaderInstance) {
        completeUploadProgress(draftId, uploaderInstance);
      }
      break;
    }

    case ActionTypes.COMPOSER_ADD_DRAFTS_AUTO_UPLOADED_IMAGE:
      addAutoUploadedImage(
        action.url,
        action.altText,
        action.source,
        action.height,
        action.width
      );
      break;

    case ActionTypes.COMPOSER_ADD_DRAFTS_AUTO_UPLOADED_GIF:
      addAutoUploadedGif(action.url, action.stillGifUrl);
      break;

    case ActionTypes.COMPOSER_UPDATE_NEXT_LINK_THUMBNAIL:
      selectNextLinkThumbnail(action.draftId);
      break;

    case ActionTypes.COMPOSER_UPDATE_PREVIOUS_LINK_THUMBNAIL:
      selectPreviousLinkThumbnail(action.draftId);
      break;

    case ActionTypes.COMPOSER_UPDATE_IMAGE_ALT_TEXT:
      updateImageAltText(action.image, action.altText);
      break;

    case ActionTypes.COMPOSER_UPDATE_UPLOADED_IMAGE_DIMENSIONS:
      updateUploadedImageDimensions(action.url, action.width, action.height);
      updateInstagramDraftsFeedback();
      break;

    case ActionTypes.COMPOSER_APPLY_OMNI_UPDATE:
      copyDraftContents();
      AppActionCreators.updateOmniboxState(false);
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFTS_THREAD:
      state.drafts.forEach((draft) =>
        updateDraftThread(draft.id, action.thread)
      );
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_THREAD:
      state.drafts.forEach((draft) =>
        updateDraftThread(draft.id, action.thread)
      );
      break;

    case ActionTypes.COMPOSER_UPDATE_DRAFT_ADD_TWEET:
      updateDraftAddTweet(action.id);
      break;

    case ActionTypes.COMPOSER_SET_ACTIVE_THREAD_ID:
      setActiveThreadId(action.threadId);
      break;

    case ActionTypes.COMPOSER_SWITCH_ACTIVE_THREAD_EDITOR:
      switchActiveThreadEditor(action.id, action.threadId);
      break;

    case ActionTypes.COMPOSER_DELETE_THREADED_DRAFT:
      deleteThreadedDraft(action.id, action.threadID);
      break;

    case ActionTypes.APP_UPDATE_OMNIBOX_STATE:
      clearOmniboxDraftWhenEnabling(action.isEnabled);
      break;

    case ActionTypes.COMPOSER_ADD_DRAFTS_AUTO_UPLOADED_VIDEO:
      video = getNewVideo(action.video);
      addSharedUploadedVideo(video);
      state.drafts.forEach((draft) => addDraftVideo(draft.id, video));
      break;

    case ActionTypes.COMPOSER_DRAFTS_PREVENT_AUTO_ATTACHING_URLS:
      state.drafts.forEach((draft) => {
        draft.urls = action.urls;
      });
      break;

    case ActionTypes.COMPOSER_FORCE_FOCUS:
      state.meta.forceEditorFocus = true;
      break;

    case ActionTypes.COMPOSER_STOP_FORCE_FOCUS:
      state.meta.forceEditorFocus = false;
      break;

    case ActionTypes.APP_RESET:
      state = getInitialState();
      break;

    case ActionTypes.COMPOSER_UPDATE_INSTAGRAM_STATE:
      updateInstagramDraftsFeedback();
      break;

    case ActionTypes.APP_SOFT_RESET:
      softResetState();
      break;

    case ActionTypes.EVENT_SHOW_SWITCH_PLAN_MODAL:
      eventShowSwitchPlanModal();
      break;

    case ActionTypes.COMPOSER_INSERT_HASHTAG_TO_CAPTION:
      insertHashtagInCaption(action.text);
      break;

    case ActionTypes.COMPOSER_UPDATE_INSTAGRAM_DRAFTS_FEEDBACK:
      updateInstagramDraftsFeedback();
      break;

    default:
      shouldEmitChange = false;
      break;
  }

  if (shouldEmitChange) ComposerStore.emitChange();

  // (uncomment to enable Redux DevTools)
  // sendToMonitor('composer', action, state);
};

AppDispatcher.register(onDispatchedPayload);

export default ComposerStore;
